/**
 * ChatPage — SENSOR.Chat ("Чат по источникам")
 *
 * Three-pane layout (left rail / center thread / right rail) that renders
 * INSIDE the existing Layout's <Outlet />.  The outer wrapper
 * .ds-chat-fullbleed tells styles.css to zero-pad .ds-content and let the
 * chat fill the remaining viewport height.
 *
 * Class hooks reused verbatim from chat.css:
 *   .ds-chat                  — 3-pane grid root
 *   .is-left-closed           — hides the sessions rail (desktop)
 *   .is-right-closed          — hides the context rail (desktop)
 *   .is-left-open             — shows sessions rail as drawer (≤900 px)
 *   .is-right-open            — shows context rail as drawer (≤1240 px)
 *   .ds-chat__drawer-scrim.is-open — backdrop for drawers
 *   .ds-chat__rail-reopen--left / --right — re-open buttons (auto-visible via CSS)
 *   data-state="thread|empty|loading|task" — controls which center view is shown
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ApiError,
  api,
  type ChatMessage,
  type ChatSession,
  type ChatSessionDetail,
  type CreateChatSessionRequest,
  type CreateScheduledTaskRequest,
  type ScheduledTaskResponse,
  type UpdateScheduledTaskRequest,
  type TgChatDialog,
  type TgFolder,
  type DialogSnapshot,
  type Userbot,
} from '@/lib/api'
import { LiquidButton } from '@/components/LiquidButton'
import { Select } from '@/components/Select'
import { useAuth } from '@/lib/auth'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function pluralRu(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10
  const m100 = n % 100
  if (m10 === 1 && m100 !== 11) return one
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few
  return many
}

function relativeActivity(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMin = Math.floor(diffMs / 60_000)
  if (diffMin < 1) return 'сейчас'
  if (diffMin < 60) return `${diffMin} мин назад`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr} ч назад`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay === 1) return 'вчера'
  return `${diffDay} дн назад`
}

function parseApiDate(iso: string): Date {
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(iso)
  return new Date(hasTimezone ? iso : `${iso}Z`)
}

function formatMessageTime(iso: string): string {
  const d = parseApiDate(iso)
  if (Number.isNaN(d.getTime())) return ''

  const now = new Date()
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()

  return new Intl.DateTimeFormat('ru-RU', {
    day: sameDay ? undefined : '2-digit',
    month: sameDay ? undefined : '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

function fullMessageTime(iso: string): string {
  const d = parseApiDate(iso)
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

/** "45 сек" / "2 мин 12 сек" — switches to minutes past 60s. */
function formatElapsed(totalSeconds: number): string {
  if (totalSeconds < 60) return `${totalSeconds} сек`
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return s === 0 ? `${m} мин` : `${m} мин ${s} сек`
}

function displayInitials(value: string, fallback = '··'): string {
  const source = value.trim()
  const words = source.split(/\s+/).filter(Boolean)
  const letters = words
    .map((word) => Array.from(word).find((char) => /[\p{L}\p{N}]/u.test(char)))
    .filter(Boolean)
  if (letters.length >= 2) return `${letters[0]}${letters[1]}`.toUpperCase()
  const chars = Array.from(source).filter((char) => /[\p{L}\p{N}]/u.test(char))
  return chars.slice(0, 2).join('').toUpperCase() || fallback
}

function srcInitials(title: string): string {
  return displayInitials(title)
}

function formatTaskSchedule(task: ScheduledTaskResponse): string {
  const time = task.schedule_time ?? '10:00'
  if (task.schedule_type === 'weekly') return `еженедельно в ${time}`
  if (task.schedule_type === 'custom') {
    const days = task.interval_days ?? 1
    return `каждые ${days} ${pluralRu(days, 'день', 'дня', 'дней')} в ${time}`
  }
  return `ежедневно в ${time}`
}

// ---------------------------------------------------------------------------
// SVG icons
// ---------------------------------------------------------------------------

const IconChat = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}>
    <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8A8.5 8.5 0 1 1 21 11.5z" />
  </svg>
)

const IconChatMark = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8A8.5 8.5 0 1 1 21 11.5z" />
  </svg>
)

const IconSend = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M7 11l5-5 5 5M12 6v14" />
  </svg>
)

const IconPlus = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M12 5v14M5 12h14" />
  </svg>
)

const IconChevronLeft = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M15 6l-6 6 6 6" />
  </svg>
)

const IconChevronRight = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M9 6l6 6-6 6" />
  </svg>
)

const IconHamburger = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M4 6h16M4 12h10M4 18h16" />
  </svg>
)

const IconPanel = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M3 5h18v14H3zM15 5v14" />
  </svg>
)

const IconSearch = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" />
  </svg>
)

const IconClose = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M6 6l12 12M18 6l-12 12" />
  </svg>
)

const IconTrash = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
    <path d="M4 7h16M9 7V5h6v2M7 7l1 15h8l1-15" />
    <path d="M10 11v6M14 11v6" />
  </svg>
)

const IconInfo = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v5M12 16h.01" />
  </svg>
)

const IconExtLink = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M7 17 17 7M9 7h8v8" />
  </svg>
)

const IconCheck = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
    <path d="M20 6 9 17l-5-5" />
  </svg>
)

const IconWarnTriangle = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M12 9v4M12 17h.01M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.7 3.86a2 2 0 0 0-3.4 0z" />
  </svg>
)

const IconAlertCircle = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v5M12 16h.01" />
  </svg>
)

const IconTelegramPlane = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M22 2 11 13M22 2 15 22l-4-9-9-4 20-7z" />
  </svg>
)

// ---------------------------------------------------------------------------
// AssistantContent formatter
// ---------------------------------------------------------------------------

/**
 * Parse inline markup within a single line of text.
 * Handles:
 *   - <a href="URL">LABEL</a>  → real <a> (only http/https hrefs)
 *   - **text**                  → <strong>
 * Everything else is kept as plain text (React auto-escapes — no XSS risk).
 * dangerouslySetInnerHTML is intentionally NOT used.
 */
// A citation extracted from the assistant body, in document order.
interface ExtractedCite {
  n: number
  url: string
  channelId: string
  msgId: string
}

// Parse a t.me URL into channel + message ids.
// Supports /c/<channelId>/<msgId> (private) and /<username>/<msgId> (public).
function parseTme(url: string): { channelId: string; msgId: string } | null {
  const m = url.match(/t\.me\/(?:c\/(\d+)\/(\d+)|([^/?#]+)\/(\d+))/i)
  if (!m) return null
  if (m[1]) return { channelId: m[1], msgId: m[2] }
  if (m[3]) return { channelId: m[3], msgId: m[4] }
  return null
}


function initials(name: string): string {
  return displayInitials(name)
}

/**
 * Parse inline markup within a single line of text.
 *   - <a href="t.me/…">…</a>    → numbered footnote button (pushes into `cites`)
 *   - <a href="http(s)://…">…</a> (non-t.me) → real <a>
 *   - **text**                   → <strong>
 * Everything else stays plain text (React auto-escapes). No dangerouslySetInnerHTML.
 */
function parseInline(
  text: string,
  keyPrefix: string,
  cites: ExtractedCite[],
): React.ReactNode[] {
  const nodes: React.ReactNode[] = []
  // Bold pattern now allows newlines inside (**…**) via [\s\S]+?
  // Match HTML links, Markdown links [label](url), and bold spans
  const pattern =
    /<a\s+href="([^"]*)"[^>]*>([\s\S]*?)<\/a>|\[([\s\S]*?)\]\((https?:\/\/[^\s\)]+)\)|\*\*([\s\S]+?)\*\*/g
  let lastIndex = 0
  let match: RegExpExecArray | null

  // Helper: emit a plain-text segment, splitting on \n to insert <br/> elements.
  // Any leftover "**" here is an UNPAIRED bold marker (paired ones were already
  // consumed by the regex) — e.g. Gemini emitting bold across a block boundary.
  // Strip them so the user never sees literal "**".
  function pushPlain(raw: string, brKeyBase: string) {
    if (!raw) return
    const parts = raw.replace(/\*\*/g, '').split('\n')
    parts.forEach((part, idx) => {
      if (idx > 0) nodes.push(<br key={`${brKeyBase}-br${idx}`} />)
      if (part) nodes.push(part)
    })
  }

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      pushPlain(text.slice(lastIndex, match.index), `${keyPrefix}-plain-${lastIndex}`)
    }

    const href = match[1] !== undefined ? match[1] : match[4]
    const label = match[1] !== undefined ? match[2] : match[3]

    if (href !== undefined) {
      const tme = parseTme(href)
      if (tme && (href.startsWith('https://') || href.startsWith('http://'))) {
        // Citation → numbered footnote referencing its source card below.
        const n = cites.length + 1
        cites.push({ n, url: href, channelId: tme.channelId, msgId: tme.msgId })
        nodes.push(
          <a
            key={`${keyPrefix}-fn-${match.index}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="ds-chat__fn"
            title="Открыть оригинал сообщения в Telegram"
          >
            {n}
          </a>,
        )
      } else if (href.startsWith('https://') || href.startsWith('http://')) {
        nodes.push(
          <a
            key={`${keyPrefix}-a-${match.index}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="ds-chat__cite-link"
          >
            {label || href}
          </a>,
        )
      } else {
        nodes.push(label || href)
      }
    } else if (match[5] !== undefined) {
      nodes.push(<strong key={`${keyPrefix}-b-${match.index}`}>{match[5]}</strong>)
    }

    lastIndex = pattern.lastIndex
  }

  if (lastIndex < text.length) {
    pushPlain(text.slice(lastIndex), `${keyPrefix}-tail-${lastIndex}`)
  }

  return nodes
}

type Block =
  | { type: 'paragraph'; lines: string[] }
  | { type: 'bullet'; items: string[] }

/**
 * Tokenize raw assistant text into a list of blocks:
 *   - Consecutive lines starting with "* " / "*   " / "- " → bullet block
 *   - Blank lines separate paragraph blocks
 *   - Single \n within a paragraph block → <br/>
 */
function tokenizeBlocks(text: string): Block[] {
  const lines = text.split('\n')
  const blocks: Block[] = []
  let paraLines: string[] = []
  let bulletItems: string[] = []

  function flushPara() {
    if (paraLines.length > 0) {
      blocks.push({ type: 'paragraph', lines: paraLines })
      paraLines = []
    }
  }

  function flushBullet() {
    if (bulletItems.length > 0) {
      blocks.push({ type: 'bullet', items: bulletItems })
      bulletItems = []
    }
  }

  for (const line of lines) {
    const bulletMatch = /^\s*[*-]\s+(.*)/.exec(line)
    if (bulletMatch) {
      // Start / continue a bullet block
      flushPara()
      bulletItems.push(bulletMatch[1])
    } else if (line.trim() === '') {
      // Blank line: flush both accumulators
      flushBullet()
      flushPara()
    } else {
      // Regular text line
      flushBullet()
      paraLines.push(line)
    }
  }

  flushBullet()
  flushPara()

  return blocks
}

/**
 * Render a paragraph block: lines are joined with '\n' so that bold spans
 * crossing line boundaries are parsed correctly by parseInline. The
 * parseInline helper converts any '\n' in plain-text segments into <br/>
 * elements, preserving soft line breaks within a paragraph.
 */
function renderParagraph(
  lines: string[],
  blockKey: string,
  cites: ExtractedCite[],
): React.ReactNode {
  const joined = lines.join('\n')
  const children = parseInline(joined, blockKey, cites)
  return <p key={blockKey}>{children}</p>
}

/**
 * Render a bullet block as <ul><li>…</li></ul>.
 * Inline markup is applied to each item.
 */
function renderBullets(
  items: string[],
  blockKey: string,
  cites: ExtractedCite[],
): React.ReactNode {
  return (
    <ul key={blockKey} className="ds-chat__answer__list">
      {items.map((item, i) => (
        <li key={`${blockKey}-li-${i}`}>{parseInline(item, `${blockKey}-li${i}`, cites)}</li>
      ))}
    </ul>
  )
}

/**
 * Top-level component: converts Telegram-style rich text into React elements.
 * NO dangerouslySetInnerHTML used anywhere.
 */
function AssistantContent({
  text,
  sourceTitles,
}: {
  text: string
  sourceTitles: string[]
}) {
  // Single pass: rendering the body fills `cites` (in document order), then the
  // grouped sources block is built from it — so footnote numbers and card ids match.
  const cleanText = (text || '')
    .replace(/\[cite:\s*[\d.,\s]+\]/gi, '')
    .replace(/\s+([.,;:])/g, '$1')
  const cites: ExtractedCite[] = []
  const blocks = tokenizeBlocks(cleanText)
  const body = blocks.map((block, i) => {
    const key = `block-${i}`
    return block.type === 'bullet'
      ? renderBullets(block.items, key, cites)
      : renderParagraph(block.lines, key, cites)
  })
  return (
    <>
      <div className="ds-chat__answer__body">{body}</div>
      {cites.length > 0 && <SourcesBlock cites={cites} sourceTitles={sourceTitles} />}
    </>
  )
}

function SourcesBlock({
  cites,
  sourceTitles,
}: {
  cites: ExtractedCite[]
  sourceTitles: string[]
}) {
  const order: string[] = []
  const byChannel = new Map<string, ExtractedCite[]>()
  for (const c of cites) {
    if (!byChannel.has(c.channelId)) {
      byChannel.set(c.channelId, [])
      order.push(c.channelId)
    }
    byChannel.get(c.channelId)!.push(c)
  }
  const distinct = order.length
  const total = cites.length

  let trustK: 'high' | 'mid' | 'low'
  let trustLabel: string
  if (total >= 3 && distinct >= 2) {
    trustK = 'high'
    trustLabel = 'Хорошо обосновано'
  } else if (total >= 2) {
    trustK = 'mid'
    trustLabel = 'Частично обосновано'
  } else {
    trustK = 'low'
    trustLabel = 'Слабо обосновано'
  }

  // We can only confidently name the source when the session has exactly one.
  const singleName = sourceTitles.length === 1 ? sourceTitles[0] : null

  return (
    <div className="ds-chat__sources">
      <div className="ds-chat__sources__head">
        <span className="ds-chat__sources__label">
          Источники · {distinct} {pluralRu(distinct, 'группа', 'группы', 'групп')} · {total}{' '}
          {pluralRu(total, 'цитата', 'цитаты', 'цитат')}
        </span>
        <span className={`ds-chat__trust ds-chat__trust--${trustK}`}>
          <i />
          {trustLabel}
        </span>
      </div>
      <div className="ds-chat__citegroups">
        {order.map((channelId) => {
          const items = byChannel.get(channelId)!
          const name = singleName ?? `Источник ${channelId.slice(-4)}`
          return (
            <div className="ds-chat__citegroup" key={channelId}>
              <div className="ds-chat__citegroup__src">
                <span className="ds-chat__citegroup__av">{initials(name)}</span>
                <b>{name}</b>
                <small>
                  {items.length} {pluralRu(items.length, 'цитата', 'цитаты', 'цитат')}
                </small>
              </div>
              <div className="ds-chat__citelist">
                {items.map((it) => (
                  <a
                    key={it.n}
                    id={`cite-${it.n}`}
                    className="ds-chat__cite"
                    href={it.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="ds-chat__cite__n">{it.n}</span>
                    <span className="ds-chat__cite__quote">сообщение #{it.msgId}</span>
                    <span className="ds-chat__cite__ext">{IconExtLink}</span>
                  </a>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Message components
// ---------------------------------------------------------------------------

function UserBubble({ content, createdAt }: { content: string; createdAt?: string }) {
  const time = createdAt ? formatMessageTime(createdAt) : ''
  const fullTime = createdAt ? fullMessageTime(createdAt) : ''

  return (
    <div className="ds-chat__msg ds-chat__msg--user">
      <div className="ds-chat__bubble-user">{content}</div>
      {time && (
        <time className="ds-chat__msg-time ds-chat__msg-time--user" dateTime={createdAt} title={fullTime}>
          {time}
        </time>
      )}
    </div>
  )
}

function AssistantBubble({
  msg,
  sourceTitles,
}: {
  msg: ChatMessage
  sourceTitles: string[]
}) {
  return (
    <div className="ds-chat__msg ds-chat__msg--ai">
      <div className="ds-chat__answer">
        <div className="ds-chat__answer__head">
          <span className="ds-chat__answer__title">
            <span className="mark">{IconChatMark}</span>
            SENSOR.Chat
          </span>
          <time className="ds-chat__msg-time" dateTime={msg.created_at} title={fullMessageTime(msg.created_at)}>
            {formatMessageTime(msg.created_at)}
          </time>
        </div>
        <AssistantContent text={msg.content} sourceTitles={sourceTitles} />
      </div>
    </div>
  )
}

function TypingIndicator() {
  return (
    <div className="ds-chat__msg ds-chat__msg--ai">
      <div className="ds-chat__answer">
        <div className="ds-chat__answer__head">
          <span className="mark">{IconChatMark}</span>
          SENSOR.Chat
        </div>
        <div className="ds-chat__typing">
          <i />
          <i />
          <i />
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// New-chat modal (3-step wizard)
// ---------------------------------------------------------------------------

interface NewChatModalProps {
  isOpen: boolean
  onClose: () => void
  onCreated: (session: ChatSession, question: string) => void
  userTimezone: string
  canExportDialogs: boolean
}

/** Return a datetime-local string (YYYY-MM-DDTHH:mm) for the current local time. */
function localDatetimeNow(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  )
}

/**
 * Convert a wall-clock datetime string (YYYY-MM-DDTHH:mm) entered in `tz`
 * to a UTC ISO-8601 string.
 *
 * Algorithm: guess the UTC ms for that wall clock, format it back in `tz` to
 * measure the offset, then subtract the offset.
 */
function wallClockToUtcIso(local: string, tz: string): string {
  const [datePart, timePart] = local.split('T')
  if (!datePart || !timePart) return new Date(local).toISOString()
  const [Y, Mo, D] = datePart.split('-').map(Number)
  const [H, Mi] = timePart.split(':').map(Number)
  const guess = Date.UTC(Y, Mo - 1, D, H, Mi)
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
    const parts = fmt.formatToParts(new Date(guess))
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0')
    // formatToParts: month/day/hour/minute are 2-digit, year is 4-digit
    const asTz = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'))
    const offset = asTz - guess
    return new Date(guess - offset).toISOString()
  } catch {
    // Fallback: treat local as browser-local
    return new Date(local).toISOString()
  }
}

interface ParsedTelegramSource {
  chat_id: number | null
  username: string | null
  topic_id: number | null
  raw_source: string
  display_title: string
  is_invite: boolean
}

function parseTelegramSourceInput(token: string): ParsedTelegramSource {
  const raw = (token || '').trim()
  if (!raw) {
    return { chat_id: null, username: null, topic_id: null, raw_source: '', display_title: '', is_invite: false }
  }

  const clean = raw.replace(/^https?:\/\//i, '')

  if (clean.startsWith('t.me/+') || clean.startsWith('telegram.me/+') || clean.includes('joinchat')) {
    return {
      chat_id: null,
      username: null,
      topic_id: null,
      raw_source: raw,
      display_title: `Приглашение ${raw}`,
      is_invite: true,
    }
  }

  const matchC = clean.match(/^(?:t\.me|telegram\.me)\/c\/(\d+)(?:\/(\d+))?(?:\/(\d+))?\/?(?:\?[^\s]*)?$/i)
  if (matchC) {
    const cidStr = matchC[1]
    const chatId = -Number(`100${cidStr}`)
    const topicId = matchC[2] ? Number(matchC[2]) : null
    const title = topicId ? `Чат ${chatId} · тема #${topicId}` : `Чат ${chatId}`
    return {
      chat_id: chatId,
      username: null,
      topic_id: topicId,
      raw_source: raw,
      display_title: title,
      is_invite: false,
    }
  }

  const matchPub = clean.match(/^(?:(?:t\.me|telegram\.me)\/)?([a-zA-Z0-9_]{4,})(?:\/(\d+))?(?:\/(\d+))?\/?(?:\?[^\s]*)?$/i)
  if (matchPub) {
    const username = matchPub[1].replace(/^@/, '')
    const topicId = matchPub[2] ? Number(matchPub[2]) : null
    const title = topicId ? `@${username} · тема #${topicId}` : `@${username}`
    return {
      chat_id: null,
      username,
      topic_id: topicId,
      raw_source: raw,
      display_title: title,
      is_invite: false,
    }
  }

  if (raw.startsWith('@')) {
    const username = raw.slice(1).trim()
    return {
      chat_id: null,
      username,
      topic_id: null,
      raw_source: `@${username}`,
      display_title: `@${username}`,
      is_invite: false,
    }
  }

  if (/^-?\d+$/.test(raw)) {
    const val = Number(raw)
    const chatId = val < 0 ? val : -Number(`100${val}`)
    return {
      chat_id: chatId,
      username: null,
      topic_id: null,
      raw_source: String(chatId),
      display_title: `Чат ${chatId}`,
      is_invite: false,
    }
  }

  if (/^[a-zA-Z0-9_]{4,32}$/.test(raw)) {
    return {
      chat_id: null,
      username: raw,
      topic_id: null,
      raw_source: `@${raw}`,
      display_title: `@${raw}`,
      is_invite: false,
    }
  }

  return {
    chat_id: null,
    username: null,
    topic_id: null,
    raw_source: raw,
    display_title: raw,
    is_invite: false,
  }
}

// Dialogs polling hook: starts polling when triggered, stops on 'ready'|'failed'|'stopped'
function useDialogPolling(enabled: boolean, activeOnly: boolean) {
  const [snapshot, setSnapshot] = useState<DialogSnapshot | null>(null)
  const [polling, setPolling] = useState(false)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    if (tickRef.current !== null) {
      clearInterval(tickRef.current)
      tickRef.current = null
    }
    setPolling(false)
  }, [])

  const cancelPollingByUser = useCallback(() => {
    stopPolling()
    setElapsedSeconds(0)
    setSnapshot((prev) =>
      prev
        ? { ...prev, status: 'stopped' }
        : { status: 'stopped', folders: [], chats: [], error: 'stopped' },
    )
  }, [stopPolling])

  const startPolling = useCallback(() => {
    setElapsedSeconds(0)
    setPolling(true)
    if (pollRef.current !== null) clearInterval(pollRef.current)
    if (tickRef.current !== null) clearInterval(tickRef.current)
    tickRef.current = setInterval(() => {
      setElapsedSeconds((s) => s + 1)
    }, 1000)
    pollRef.current = setInterval(() => {
      api.getDialogs(activeOnly).then((snap) => {
        setSnapshot(snap)
        if (snap.status === 'ready' || snap.status === 'failed') {
          stopPolling()
        }
      }).catch(() => {/* swallow */})
    }, 3000)
  }, [activeOnly, stopPolling])

  const failPolling = useCallback((error: string) => {
    stopPolling()
    setElapsedSeconds(0)
    setSnapshot({
      status: 'failed',
      folders: [],
      chats: [],
      error,
    })
  }, [stopPolling])

  // Fetch once on enable
  useEffect(() => {
    if (!enabled) return
    api.getDialogs(activeOnly).then((snap) => {
      setSnapshot(snap)
    }).catch(() => {/* swallow */})
  }, [enabled, activeOnly])

  // Cleanup on unmount
  useEffect(() => () => stopPolling(), [stopPolling])

  return { snapshot, polling, startPolling, failPolling, stopPolling, cancelPollingByUser, elapsedSeconds }
}

function dialogSelectionKey(chat: TgChatDialog): string {
  const base = chat.chat_id ? String(chat.chat_id) : (chat.username || chat.raw_source || '0')
  const topic = chat.topic_id ? `:${chat.topic_id}` : ''
  return `${chat.session_id ?? 0}:${base}${topic}`
}

function dialogSnapshotErrorText(error?: string | null): string {
  if (error === 'stopped') {
    return 'Загрузка списка чатов остановлена. Вы можете ввести ссылку на источник вручную или обновить список заново.'
  }
  if (!error) return 'Не удалось загрузить список чатов.'
  const normalized = error.toLowerCase()
  if (
    error === 'telegram_session_invalid' ||
    normalized.includes('key is not registered') ||
    normalized.includes('auth key')
  ) {
    return 'Telegram-сессия активного юзербота устарела. Перепривяжите юзербота в настройках и попробуйте снова.'
  }
  if (error === 'no_userbot') {
    return 'Юзербот недоступен. Привяжите Telegram-аккаунт в настройках.'
  }
  return `Не удалось загрузить список чатов: ${error}`
}

const DIALOG_PAGE_SIZE = 8

interface DialogPickerTabProps {
  monitoredOnly: boolean
  snapshot: DialogSnapshot | null
  polling: boolean
  elapsedSeconds: number
  onRefresh: () => void
  onStopLoading?: () => void
  selectedChats: Map<string, TgChatDialog>
  setSelectedChats: (m: Map<string, TgChatDialog>) => void
  /** When set, only show chats/folders belonging to this userbot session. null = all userbots. */
  sessionFilter: number | null
  /** Switch to the "Все мои чаты" tab (used when a folder has no added sources). */
  onShowAll: () => void
  onExport?: () => void
  exporting?: boolean
  exportError?: string
}

function DialogPickerTab({
  monitoredOnly,
  snapshot,
  polling,
  elapsedSeconds,
  onRefresh,
  onStopLoading,
  selectedChats,
  setSelectedChats,
  sessionFilter,
  onShowAll,
  onExport,
  exporting = false,
  exportError = '',
}: DialogPickerTabProps) {
  const [activeFolderKey, setActiveFolderKey] = useState<string | null>(null)
  const [dialogSearch, setDialogSearch] = useState('')
  const [dialogPage, setDialogPage] = useState(0)

  const isReady = snapshot?.status === 'ready'

  // Check if dialogSearch contains a Telegram link, topic URL, @username, or chat ID
  const parsedSearchSource = useMemo<TgChatDialog | null>(() => {
    const q = dialogSearch.trim()
    if (!q) return null
    if (
      q.includes('t.me/') ||
      q.includes('telegram.me/') ||
      q.includes('/') ||
      q.startsWith('@') ||
      q.startsWith('https://') ||
      q.startsWith('http://') ||
      /^-?\d+$/.test(q)
    ) {
      const parsed = parseTelegramSourceInput(q)
      if (parsed && (parsed.username || parsed.topic_id || parsed.chat_id || parsed.is_invite)) {
        const targetSessionId =
          sessionFilter ??
          snapshot?.chats?.find((c) => c.session_id)?.session_id ??
          null
        const targetLabel =
          snapshot?.chats?.find((c) => (c.session_id ?? null) === targetSessionId)?.userbot_label ??
          null

        const matchedChat = snapshot?.chats?.find((c) => {
          if (parsed.chat_id && c.chat_id === parsed.chat_id) return true
          if (parsed.username && (c.username ?? '').toLowerCase() === parsed.username.toLowerCase()) return true
          return false
        })

        const title = parsed.topic_id
          ? `${matchedChat?.title || (parsed.username ? `@${parsed.username}` : parsed.display_title)} · тема #${parsed.topic_id}`
          : matchedChat?.title || parsed.display_title

        return {
          chat_id: matchedChat ? matchedChat.chat_id : (parsed.chat_id ?? 0),
          title,
          username: parsed.username ?? matchedChat?.username ?? null,
          chat_type: parsed.topic_id ? 'topic' : (matchedChat?.chat_type ?? 'channel'),
          folder_ids: matchedChat?.folder_ids ?? [],
          monitored: matchedChat?.monitored ?? false,
          session_id: matchedChat?.session_id ?? targetSessionId,
          userbot_label: matchedChat?.userbot_label ?? targetLabel,
          is_active_userbot: matchedChat?.is_active_userbot ?? false,
          topic_id: parsed.topic_id ?? null,
          raw_source: parsed.raw_source || q,
        }
      }
    }
    return null
  }, [dialogSearch, sessionFilter, snapshot])

  // Chats for the active userbot filter (no monitored filter yet).
  const sessionChats: TgChatDialog[] = (snapshot?.chats ?? []).filter(
    (c) => sessionFilter === null || (c.session_id ?? 0) === sessionFilter,
  )

  const matchesFolderSearch = (c: TgChatDialog): boolean => {
    if (activeFolderKey !== null) {
      const [sid, fid] = activeFolderKey.split(':')
      if (String(c.session_id ?? 0) !== sid || !c.folder_ids.includes(Number(fid))) return false
    }
    const q = dialogSearch.toLowerCase()
    if (!q) return true
    return (
      c.title.toLowerCase().includes(q) ||
      (c.username ?? '').toLowerCase().includes(q)
    )
  }

  const baseChats: TgChatDialog[] = sessionChats.filter((c) =>
    monitoredOnly ? c.monitored : true,
  )
  const filteredDialogs: TgChatDialog[] = baseChats.filter(matchesFolderSearch)

  // On the "Добавлены" tab, how many chats match the current folder/search but are
  // hidden because they aren't added as monitored sources yet. Drives the hint
  // that points the user to "Все мои чаты" (this confused even the founder).
  const allMatchesCount = monitoredOnly
    ? sessionChats.filter(matchesFolderSearch).length
    : filteredDialogs.length
  const hiddenByMonitored = Math.max(0, allMatchesCount - filteredDialogs.length)

  const totalDialogPages = Math.max(1, Math.ceil(filteredDialogs.length / DIALOG_PAGE_SIZE))
  const safeDialogPage = Math.min(dialogPage, totalDialogPages - 1)
  const dialogPageSlice = filteredDialogs.slice(
    safeDialogPage * DIALOG_PAGE_SIZE,
    safeDialogPage * DIALOG_PAGE_SIZE + DIALOG_PAGE_SIZE,
  )

  function toggleDialog(chat: TgChatDialog) {
    const next = new Map(selectedChats)
    const key = dialogSelectionKey(chat)
    if (next.has(key)) {
      next.delete(key)
    } else {
      next.set(key, chat)
    }
    setSelectedChats(next)
  }

  function isChatInFolder(chat: TgChatDialog, folderKey: string): boolean {
    const [sid, fid] = folderKey.split(':')
    return String(chat.session_id ?? 0) === sid && chat.folder_ids.includes(Number(fid))
  }

  if (!isReady) {
    const isLoading = (polling && snapshot?.status !== 'failed') || snapshot?.status === 'requested' || snapshot?.status === 'running'
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '32px 16px', textAlign: 'center' }}>
        {isLoading ? (
          <>
            <div className="ds-chat__loading__spin" style={{ width: 28, height: 28, margin: 0 }} />
            <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>Загружаю ваши чаты из Telegram…</span>
            <span style={{ maxWidth: 360, fontSize: 12, color: 'var(--muted)', lineHeight: 1.45 }}>
              Обычно это занимает несколько секунд, иногда до минуты: SENSOR подключается к выбранному юзерботу и получает список его групп, каналов и папок.
            </span>
            <div style={{ width: 260, height: 6, borderRadius: 999, overflow: 'hidden', background: 'rgba(11,15,20,0.08)' }}>
              <div
                style={{
                  width: `${Math.min(92, 18 + elapsedSeconds * 2)}%`,
                  height: '100%',
                  borderRadius: 999,
                  background: 'var(--accent)',
                  transition: 'width 0.8s ease',
                }}
              />
            </div>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>
              Прошло: {formatElapsed(elapsedSeconds)}
            </span>
            {onStopLoading && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={onStopLoading}
                style={{ marginTop: 6 }}
              >
                Остановить загрузку чатов
              </button>
            )}
          </>
        ) : (
          <>
            <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
              {snapshot?.status === 'failed' || snapshot?.status === 'stopped'
                ? dialogSnapshotErrorText(snapshot.error ?? (snapshot.status === 'stopped' ? 'stopped' : null))
                : 'Список чатов ещё не загружен'}
            </span>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={onRefresh}
            >
              Обновить список чатов
            </button>
          </>
        )}
      </div>
    )
  }

  if (monitoredOnly && baseChats.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '32px 16px', textAlign: 'center' }}>
        <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
          Нет мониторируемых чатов — добавьте источники в настройках проекта.
        </span>
      </div>
    )
  }

  const folders: TgFolder[] = (snapshot?.folders ?? []).filter(
    (f) => sessionFilter === null || (f.session_id ?? 0) === sessionFilter,
  )
  const activeFolder = activeFolderKey
    ? folders.find((f) => `${f.session_id ?? 0}:${f.id}` === activeFolderKey) ?? null
    : null
  const activeFolderChats = activeFolderKey
    ? baseChats.filter((chat) => isChatInFolder(chat, activeFolderKey))
    : []
  const activeFolderSelected = activeFolderChats.filter((chat) =>
    selectedChats.has(dialogSelectionKey(chat)),
  ).length
  const activeFolderFullySelected =
    activeFolderChats.length > 0 && activeFolderSelected === activeFolderChats.length

  function selectActiveFolderChats() {
    if (!activeFolderKey || activeFolderChats.length === 0) return
    const next = new Map(selectedChats)
    for (const chat of activeFolderChats) {
      next.set(dialogSelectionKey(chat), chat)
    }
    setSelectedChats(next)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Refresh control — re-enumerates folders/chats from Telegram. Always
          available while the list is loaded (the list is cached otherwise). */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={onRefresh}
          disabled={polling}
          style={{ fontSize: 12, padding: '4px 10px', width: 'fit-content' }}
          title="Заново получить список папок и чатов из Telegram"
        >
          {polling ? `Обновляю… ${formatElapsed(elapsedSeconds)}` : '↻ Обновить чаты'}
        </button>
        {onExport && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {exportError && (
              <span style={{ fontSize: 12, color: 'var(--danger-ink)' }}>
                {exportError}
              </span>
            )}
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={onExport}
              disabled={exporting}
              style={{ fontSize: 12, padding: '4px 10px', width: 'fit-content' }}
              title="Скачать полный список Telegram-чатов из текущего snapshot"
            >
              {exporting ? 'Экспорт…' : 'Выгрузить CSV'}
            </button>
          </div>
        )}
      </div>
      {/* Folder filter chips */}
      {folders.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '0 0 4px' }}>
          <button
            type="button"
            className={`ds-chat__preset${activeFolderKey === null ? ' is-on' : ''}`}
            onClick={() => { setActiveFolderKey(null); setDialogPage(0) }}
          >
            Все
          </button>
          {folders.map((f) => (
            <button
              key={`${f.session_id ?? 0}:${f.id}`}
              type="button"
              className={`ds-chat__preset${activeFolderKey === `${f.session_id ?? 0}:${f.id}` ? ' is-on' : ''}`}
              onClick={() => { setActiveFolderKey(`${f.session_id ?? 0}:${f.id}`); setDialogPage(0) }}
            >
              {f.title}{f.userbot_label ? ` · ${f.userbot_label}` : ''}
            </button>
          ))}
        </div>
      )}
      {activeFolder && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '0 0 4px', flexWrap: 'wrap' }}>
          <span style={{ minWidth: 0, fontSize: 12, color: 'var(--muted)' }}>
            {activeFolder.title}: {activeFolderChats.length} {pluralRu(activeFolderChats.length, 'чат', 'чата', 'чатов')}
          </span>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={selectActiveFolderChats}
            disabled={activeFolderChats.length === 0 || activeFolderFullySelected}
            style={{ flexShrink: 0 }}
            title="Добавить все чаты из выбранной Telegram-папки"
          >
            {activeFolderFullySelected
              ? 'Все из папки выбраны'
              : `Добавить все из папки (${activeFolderChats.length})`}
          </button>
        </div>
      )}
      {/* Search */}
      <div className="ds-chat__modal-search">
        {IconSearch}
        <input
          type="search"
          placeholder="Поиск по названию или ссылке (например, https://t.me/visadtv/4738)…"
          autoComplete="off"
          value={dialogSearch}
          onChange={(e) => { setDialogSearch(e.target.value); setDialogPage(0) }}
        />
      </div>
      {/* Hint when "Добавлены" hides chats that exist under "Все мои чаты" */}
      {monitoredOnly && hiddenByMonitored > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
          padding: '8px 12px', borderRadius: 8,
          background: 'var(--accent-subtle, oklch(0.97 0.03 250))',
          border: '1px solid var(--accent, oklch(0.55 0.18 250))',
        }}>
          <span style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.4 }}>
            {filteredDialogs.length === 0
              ? `Здесь только добавленные источники, а их в этой папке нет. Всего чатов: ${hiddenByMonitored}.`
              : `Показаны только добавленные источники. Ещё ${hiddenByMonitored} ${pluralRu(hiddenByMonitored, 'чат', 'чата', 'чатов')} в этой папке.`}
          </span>
          <button type="button" className="btn btn--primary btn--sm" style={{ flexShrink: 0 }} onClick={onShowAll}>
            Все мои чаты
          </button>
        </div>
      )}
      {/* Chat list */}
      <div className="ds-chat__picker">
        {parsedSearchSource && (
          <div
            className={`ds-chat__src${selectedChats.has(dialogSelectionKey(parsedSearchSource)) ? ' is-on' : ''}`}
            onClick={() => toggleDialog(parsedSearchSource)}
            style={{
              border: '1.5px solid var(--accent, #0284c7)',
              background: selectedChats.has(dialogSelectionKey(parsedSearchSource))
                ? 'var(--surface-3, #e0f2fe)'
                : 'rgba(2, 132, 199, 0.06)',
              marginBottom: 8,
              cursor: 'pointer',
            }}
          >
            <span className="ds-chat__check">{IconCheck}</span>
            <span
              className="ds-chat__src__avatar"
              style={{
                background: 'var(--accent, #0284c7)',
                color: '#fff',
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {parsedSearchSource.topic_id ? '#' : '🔗'}
            </span>
            <span className="ds-chat__src__meta">
              <b>{parsedSearchSource.title}</b>
              <small style={{ color: 'var(--accent, #0284c7)', fontWeight: 500 }}>
                {parsedSearchSource.topic_id
                  ? `Тема форума #${parsedSearchSource.topic_id} · Прямая ссылка`
                  : 'Прямая ссылка на источник'}
                {parsedSearchSource.userbot_label ? ` · ${parsedSearchSource.userbot_label}` : ''}
              </small>
            </span>
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--accent, #0284c7)',
                marginLeft: 'auto',
                padding: '2px 8px',
                borderRadius: 4,
                background: selectedChats.has(dialogSelectionKey(parsedSearchSource))
                  ? '#fff'
                  : 'transparent',
              }}
            >
              {selectedChats.has(dialogSelectionKey(parsedSearchSource)) ? '✓ Выбран' : '+ Выбрать'}
            </span>
          </div>
        )}

        {dialogPageSlice.length === 0 ? (
          parsedSearchSource ? null : (
            <div className="ds-chat__picker__empty">
              {monitoredOnly && hiddenByMonitored > 0 ? 'Нет добавленных источников в этой папке' : 'Ничего не найдено'}
            </div>
          )
        ) : (
          dialogPageSlice.map((chat) => {
            const isOn = selectedChats.has(dialogSelectionKey(chat))
            return (
              <div
                key={dialogSelectionKey(chat)}
                className={`ds-chat__src${isOn ? ' is-on' : ''}`}
                onClick={() => toggleDialog(chat)}
              >
                <span className="ds-chat__check">{IconCheck}</span>
                <span className="ds-chat__src__avatar">{srcInitials(chat.title)}</span>
                <span className="ds-chat__src__meta">
                  <b>{chat.title}</b>
                  <small>
                    {[chat.username ? `@${chat.username}` : chat.chat_type, chat.userbot_label]
                      .filter(Boolean)
                      .join(' · ')}
                  </small>
                </span>
              </div>
            )
          })
        )}
      </div>
      {/* Pagination */}
      <div className="ds-chat__picker-foot" style={{ padding: '4px 0 0' }}>
        <span className="sel" />
        <div className="ds-chat__pager">
          <button
            type="button"
            aria-label="Назад"
            disabled={safeDialogPage === 0}
            onClick={() => setDialogPage((p) => Math.max(0, p - 1))}
          >
            {IconChevronLeft}
          </button>
          <span>{safeDialogPage + 1} / {totalDialogPages}</span>
          <button
            type="button"
            aria-label="Вперёд"
            disabled={safeDialogPage >= totalDialogPages - 1}
            onClick={() => setDialogPage((p) => p + 1)}
          >
            {IconChevronRight}
          </button>
        </div>
      </div>
    </div>
  )
}

function NewChatModal({
  isOpen,
  onClose,
  onCreated,
  userTimezone,
  canExportDialogs,
}: NewChatModalProps) {
  const queryClient = useQueryClient()

  // Step order: 1=Источники, 2=Объём, 3=Вопрос
  const [step, setStep] = useState(1)
  const [question, setQuestion] = useState('')
  // Tab in step 1: 'monitored' | 'all'
  const [sourceTab, setSourceTab] = useState<'monitored' | 'all'>('all')
  // null = all userbots; otherwise filter the chat list to this session id.
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null)
  // Unified selection: all selections go into extra_chats
  const [selectedChats, setSelectedChats] = useState<Map<string, TgChatDialog>>(new Map())
  const [volumeMode, setVolumeMode] = useState<'count' | 'period' | 'datetime'>('period')
  const [countVal, setCountVal] = useState(500)
  const [periodPreset, setPeriodPreset] = useState<'1d' | '3d' | '7d' | '30d'>('30d')
  const [datetimeVal, setDatetimeVal] = useState(localDatetimeNow)
  const [exportingDialogs, setExportingDialogs] = useState(false)
  const [exportDialogError, setExportDialogError] = useState('')

  // Userbots for the per-userbot switcher (rendered only when >1 userbot).
  const userbotsQuery = useQuery({
    queryKey: ['me', 'userbots'],
    queryFn: () => api.getUserbots(),
    enabled: isOpen,
  })
  const userbots: Userbot[] = userbotsQuery.data?.userbots ?? []

  // Dialog snapshot shared across both tabs. Always enumerate ALL userbots;
  // the per-userbot switcher filters client-side by session id.
  const { snapshot, polling, startPolling, failPolling, elapsedSeconds, cancelPollingByUser } = useDialogPolling(isOpen, false)
  const [manualSourceInput, setManualSourceInput] = useState('')
  const [manualSourceMsg, setManualSourceMsg] = useState<{ text: string; error?: boolean } | null>(null)

  function handleAddManualSource() {
    const raw = manualSourceInput.trim()
    if (!raw) return

    const tokens = raw.split(/[\n,]+/).map((t) => t.trim()).filter(Boolean)
    if (tokens.length === 0) return

    const nextSelected = new Map(selectedChats)
    let addedCount = 0

    const targetSessionId = selectedSessionId ?? userbots[0]?.id ?? null
    const targetUserbot = userbots.find((u) => u.id === targetSessionId)

    const snapChats = snapshot?.chats ?? []
    const byUsername = new Map<string, TgChatDialog>()
    const byChatId = new Map<number, TgChatDialog>()
    for (const c of snapChats) {
      if (c.chat_id) byChatId.set(Number(c.chat_id), c)
      if (c.username) byUsername.set(c.username.toLowerCase().replace(/^@/, ''), c)
      for (const alias of ((c as any).usernames || [])) {
        if (alias) byUsername.set(String(alias).toLowerCase().replace(/^@/, ''), c)
      }
    }

    for (const token of tokens) {
      const parsed = parseTelegramSourceInput(token)
      let matchedChat: TgChatDialog | undefined
      if (parsed.chat_id && byChatId.has(parsed.chat_id)) {
        matchedChat = byChatId.get(parsed.chat_id)
      } else if (parsed.username && byUsername.has(parsed.username.toLowerCase())) {
        matchedChat = byUsername.get(parsed.username.toLowerCase())
      }

      let chatToAdd: TgChatDialog
      if (matchedChat) {
        const titleWithTopic = parsed.topic_id
          ? `${matchedChat.title} · тема #${parsed.topic_id}`
          : matchedChat.title
        chatToAdd = {
          ...matchedChat,
          title: titleWithTopic,
          topic_id: parsed.topic_id,
          raw_source: parsed.raw_source || token,
        }
      } else {
        const baseTitle = parsed.display_title || parsed.raw_source || token
        chatToAdd = {
          chat_id: parsed.chat_id ?? 0,
          title: baseTitle,
          username: parsed.username,
          chat_type: 'supergroup',
          folder_ids: [],
          monitored: false,
          session_id: targetSessionId,
          userbot_label: targetUserbot?.label ?? null,
          is_active_userbot: true,
          topic_id: parsed.topic_id,
          raw_source: parsed.raw_source || token,
        }
      }

      const key = dialogSelectionKey(chatToAdd)
      nextSelected.set(key, chatToAdd)
      addedCount++
    }

    setSelectedChats(nextSelected)
    setManualSourceInput('')
    setManualSourceMsg({
      text: addedCount === 1 ? 'Источник добавлен' : `Добавлено источников: ${addedCount}`,
      error: false,
    })
    setTimeout(() => setManualSourceMsg(null), 4000)
  }

  const refreshDialogSnapshot = useCallback(() => {
    void api.refreshDialogs(false).then((res) => {
      if (res.status === 'failed') {
        failPolling(res.error ?? 'no_userbot')
        return
      }
      startPolling()
    }).catch(() => {
      failPolling('error')
    })
  }, [failPolling, startPolling])

  // Auto-trigger refresh if snapshot is not ready when modal opens
  useEffect(() => {
    if (!isOpen) return
    const status = snapshot?.status
    if (status === undefined || status === null) return
    if ((status === 'requested' || status === 'running') && !polling) {
      startPolling()
      return
    }
    if ((status === 'none' || status === 'stale') && !polling) {
      refreshDialogSnapshot()
    }
  }, [isOpen, snapshot?.status, polling, startPolling, refreshDialogSnapshot])

  // Default tab: 'monitored' if any monitored chats exist, else 'all'
  // Run once when snapshot becomes ready
  const tabDefaultedRef = useRef(false)
  useEffect(() => {
    if (!isOpen) return
    if (tabDefaultedRef.current) return
    if (snapshot?.status === 'ready') {
      tabDefaultedRef.current = true
      // Default to "Все мои чаты" — picking sources for a new chat usually means
      // any chat, not only the ones already added as Sensor.Leads sources.
      setSourceTab('all')
    }
  }, [isOpen, snapshot])

  const createMutation = useMutation({
    mutationFn: (data: CreateChatSessionRequest) => api.createChatSession(data),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['chat', 'sessions'] })
      onCreated(res.session, question.trim())
    },
  })

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setStep(1)
      setQuestion('')
      setSourceTab('all')
      setSelectedSessionId(null)
      setSelectedChats(new Map())
      setManualSourceInput('')
      setManualSourceMsg(null)
      setVolumeMode('period')
      setCountVal(500)
      setPeriodPreset('30d')
      setDatetimeVal(localDatetimeNow())
      tabDefaultedRef.current = false
    }
  }, [isOpen])

  function handleRefresh() {
    setExportDialogError('')
    refreshDialogSnapshot()
  }

  async function handleExportDialogs() {
    setExportingDialogs(true)
    setExportDialogError('')
    try {
      const blob = await api.exportDialogs(false)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `sensor_telegram_dialogs_${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setExportDialogError('Сначала обновите список чатов.')
      } else if (err instanceof ApiError && err.status === 403) {
        setExportDialogError('Доступно на платном Pro.')
      } else {
        setExportDialogError('Не удалось скачать CSV.')
      }
    } finally {
      setExportingDialogs(false)
    }
  }

  function handleSelectUserbot(sessionId: number | null) {
    setSelectedSessionId(sessionId)
    setSelectedChats(new Map())
    tabDefaultedRef.current = false
  }

  const totalSelected = selectedChats.size

  function canProceed(): boolean {
    if (step === 1) return totalSelected > 0
    if (step === 2) return true
    // step 3 — question required
    return question.trim().length > 0
  }

  function handleNext() {
    if (step < 3) {
      setStep((s) => s + 1)
    } else {
      const periodDaysMap: Record<string, number> = { '1d': 1, '3d': 3, '7d': 7, '30d': 30 }
      let scope: CreateChatSessionRequest['scope']
      if (volumeMode === 'count') {
        scope = { mode: 'count', count: countVal }
      } else if (volumeMode === 'datetime') {
        scope = {
          mode: 'datetime',
          cutoff_iso: wallClockToUtcIso(datetimeVal, userTimezone),
        }
      } else {
        scope = { mode: 'period', period_days: periodDaysMap[periodPreset] ?? 3 }
      }
      // Always send group_ids: [] — all selections go into extra_chats
      const fallbackSessionId = selectedSessionId ?? Array.from(selectedChats.values())[0]?.session_id ?? userbots[0]?.id ?? null
      createMutation.mutate({
        theme: question.trim(),
        group_ids: [],
        extra_chats: Array.from(selectedChats.values()).map((c) => ({
          chat_id: c.chat_id || 0,
          title: c.title,
          username: c.username,
          session_id: c.session_id ?? fallbackSessionId,
          topic_id: c.topic_id ?? null,
          raw_source: c.raw_source ?? null,
        })),
        scope,
      })
    }
  }

  function handleBack() {
    if (step > 1) setStep((s) => s - 1)
  }

  function handleScrimClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose()
  }

  useEffect(() => {
    if (createMutation.isSuccess) onClose()
  }, [createMutation.isSuccess, onClose])

  if (!isOpen) return null

  const stepLabels = ['Источники', 'Объём', 'Вопрос']

  return (
    <>
      <div className={`ds-chat__scrim is-open`} onClick={onClose} />
      <div
        className="ds-chat__modal is-open"
        role="dialog"
        aria-modal
        aria-label="Новый чат по источникам"
        onClick={handleScrimClick}
      >
        <div className="ds-chat__modal-card">
          {/* Header */}
          <div className="ds-chat__modal-head">
            <h2>Новый чат по&nbsp;источникам</h2>
            <button type="button" className="ds-chat__icon-btn" onClick={onClose} aria-label="Закрыть">
              {IconClose}
            </button>
          </div>

          {/* Step indicator */}
          <div className="ds-chat__steps" id="nc-steps">
            {([1, 2, 3] as const).map((n, idx) => {
              const isActive = step === n
              const isDone = step > n
              return (
                <React.Fragment key={n}>
                  {idx > 0 && <span className="ds-chat__step__line" />}
                  <div
                    className={`ds-chat__step${isActive ? ' is-active' : ''}${isDone ? ' is-done' : ''}`}
                  >
                    <span className="n">{n}</span>
                    {stepLabels[idx]}
                  </div>
                </React.Fragment>
              )
            })}
          </div>

          {/* Body */}
          <div className="ds-chat__modal-body">
            {/* Step 1 — Источники */}
            <div className={`ds-chat__pane${step === 1 ? ' is-active' : ''}`}>
              {/* Manual source input */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  padding: '10px 12px',
                  background: 'var(--surface-2, #f9fafb)',
                  borderRadius: 8,
                  border: '1px solid var(--line-2, #e5e7eb)',
                  marginBottom: 12,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>
                    Добавить чат, тему или канал вручную:
                  </span>
                  {manualSourceMsg && (
                    <span
                      style={{
                        fontSize: 12,
                        color: manualSourceMsg.error ? 'var(--danger, #b42318)' : 'var(--accent, #0284c7)',
                      }}
                    >
                      {manualSourceMsg.text}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    placeholder="Например: https://t.me/visadtv/4738, @channel или ID"
                    value={manualSourceInput}
                    onChange={(e) => setManualSourceInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleAddManualSource()
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '7px 10px',
                      fontSize: 13,
                      border: '1px solid var(--line-2, #d1d5db)',
                      borderRadius: 6,
                      background: '#fff',
                      color: 'var(--ink)',
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn--primary btn--sm"
                    disabled={!manualSourceInput.trim()}
                    onClick={handleAddManualSource}
                    style={{ flexShrink: 0 }}
                  >
                    Добавить
                  </button>
                </div>
              </div>

              {/* Selected custom / extra sources chips */}
              {selectedChats.size > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>
                      Выбрано источников: {selectedChats.size}
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedChats(new Map())}
                      style={{ background: 'transparent', border: 'none', color: 'var(--muted)', fontSize: 11, cursor: 'pointer' }}
                    >
                      Очистить все
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', maxHeight: 85, overflowY: 'auto' }}>
                    {Array.from(selectedChats.values()).map((chat) => {
                      const key = dialogSelectionKey(chat)
                      return (
                        <span
                          key={key}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '3px 8px',
                            borderRadius: 6,
                            background: 'var(--surface-3, #e0f2fe)',
                            color: 'var(--ink, #0369a1)',
                            fontSize: 12,
                            border: '1px solid var(--line, #bae6fd)',
                          }}
                        >
                          <span style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {chat.title}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const next = new Map(selectedChats)
                              next.delete(key)
                              setSelectedChats(next)
                            }}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              color: 'inherit',
                              padding: '0 2px',
                              fontSize: 13,
                              lineHeight: 1,
                            }}
                            title="Удалить источник"
                          >
                            ×
                          </button>
                        </span>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Top row: segmented tabs (left) + per-userbot switcher (right) */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                {/* Segmented control tabs */}
                <div style={{ display: 'flex', gap: 0, border: '1px solid var(--line-2)', borderRadius: 8, overflow: 'hidden', width: 'fit-content', flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={() => setSourceTab('all')}
                    style={{
                      padding: '6px 14px',
                      fontSize: 13,
                      fontWeight: sourceTab === 'all' ? 600 : 400,
                      background: sourceTab === 'all' ? 'var(--accent)' : 'transparent',
                      color: sourceTab === 'all' ? '#fff' : 'var(--ink-2)',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                    }}
                  >
                    Все мои чаты
                  </button>
                  <button
                    type="button"
                    onClick={() => setSourceTab('monitored')}
                    style={{
                      padding: '6px 14px',
                      fontSize: 13,
                      fontWeight: sourceTab === 'monitored' ? 600 : 400,
                      background: sourceTab === 'monitored' ? 'var(--accent)' : 'transparent',
                      color: sourceTab === 'monitored' ? '#fff' : 'var(--ink-2)',
                      border: 'none',
                      borderLeft: '1px solid var(--line-2)',
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                    }}
                  >
                    Только источники проектов
                  </button>
                </div>

                {/* Per-userbot switcher — only meaningful with more than one userbot */}
                {userbots.length > 1 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center', flex: 1, minWidth: 220 }}>
                    <span style={{ fontSize: 12, color: 'var(--muted)', marginRight: 2 }}>
                      Юзербот:
                    </span>
                    <button
                      type="button"
                      className={`ds-chat__preset${selectedSessionId === null ? ' is-on' : ''}`}
                      onClick={() => handleSelectUserbot(null)}
                    >
                      Все
                    </button>
                    {userbots.map((ub) => (
                      <button
                        key={ub.id}
                        type="button"
                        className={`ds-chat__preset${selectedSessionId === ub.id ? ' is-on' : ''}`}
                        onClick={() => handleSelectUserbot(ub.id)}
                      >
                        {ub.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Both tabs use the dialog snapshot; DialogPickerTab filters by monitoredOnly + sessionFilter */}
              <DialogPickerTab
                monitoredOnly={sourceTab === 'monitored'}
                snapshot={snapshot}
                polling={polling}
                elapsedSeconds={elapsedSeconds}
                onRefresh={handleRefresh}
                onStopLoading={cancelPollingByUser}
                selectedChats={selectedChats}
                setSelectedChats={setSelectedChats}
                sessionFilter={selectedSessionId}
                onShowAll={() => setSourceTab('all')}
                onExport={canExportDialogs ? handleExportDialogs : undefined}
                exporting={exportingDialogs}
                exportError={exportDialogError}
              />
              <div className="ds-chat__picker-foot" style={{ marginTop: 8 }}>
                <span className="sel">Выбрано: {totalSelected}</span>
              </div>
            </div>

            {/* Step 2 — Объём */}
            <div className={`ds-chat__pane${step === 2 ? ' is-active' : ''}`}>
              <div className="ds-chat__radio">
                {/* Count card */}
                <div
                  className={`ds-chat__radio-card${volumeMode === 'count' ? ' is-on' : ''}`}
                  data-mode="count"
                  onClick={() => setVolumeMode('count')}
                >
                  <span className="ds-chat__radio-card__dot" />
                  <div>
                    <b>Последние N сообщений</b>
                    <small>
                      Соберём заданное число последних сообщений из каждой группы.
                    </small>
                    <div className="ds-chat__radio-card__controls">
                      <span className="ds-chat__num">
                        сообщений:{' '}
                        <input
                          type="number"
                          value={countVal}
                          min={50}
                          step={50}
                          onClick={(e) => {
                            e.stopPropagation()
                            setVolumeMode('count')
                          }}
                          onChange={(e) => setCountVal(Number(e.target.value))}
                        />
                      </span>
                    </div>
                  </div>
                </div>

                {/* Period card */}
                <div
                  className={`ds-chat__radio-card${volumeMode === 'period' ? ' is-on' : ''}`}
                  data-mode="period"
                  onClick={() => setVolumeMode('period')}
                >
                  <span className="ds-chat__radio-card__dot" />
                  <div>
                    <b>Период</b>
                    <small>
                      Соберём все сообщения за выбранный промежуток времени.
                    </small>
                    <div className="ds-chat__radio-card__controls">
                      {(['1d', '3d', '7d', '30d'] as const).map((p) => {
                        const labels = { '1d': 'за день', '3d': 'за 3 дня', '7d': 'за неделю', '30d': 'за месяц' }
                        return (
                          <button
                            key={p}
                            type="button"
                            className={`ds-chat__preset${periodPreset === p && volumeMode === 'period' ? ' is-on' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation()
                              setVolumeMode('period')
                              setPeriodPreset(p)
                            }}
                          >
                            {labels[p]}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </div>

                {/* Datetime card */}
                <div
                  className={`ds-chat__radio-card${volumeMode === 'datetime' ? ' is-on' : ''}`}
                  data-mode="datetime"
                  onClick={() => setVolumeMode('datetime')}
                >
                  <span className="ds-chat__radio-card__dot" />
                  <div>
                    <b>Свой период (с даты/времени)</b>
                    <small>
                      Соберём сообщения начиная с указанной даты и времени.
                    </small>
                    <div className="ds-chat__radio-card__controls">
                      <input
                        type="datetime-local"
                        value={datetimeVal}
                        onClick={(e) => {
                          e.stopPropagation()
                          setVolumeMode('datetime')
                        }}
                        onChange={(e) => {
                          setVolumeMode('datetime')
                          setDatetimeVal(e.target.value)
                        }}
                        style={{ fontSize: 13, padding: '2px 6px', borderRadius: 6, border: '1px solid var(--line-2)' }}
                      />
                      <span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 6 }}>
                        {userTimezone}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="ds-chat__composer__hint" style={{ margin: 0 }}>
                {IconInfo}
                Чем больше объём, тем дольше сбор и выше расход ingest-кредитов.
              </div>
            </div>

            {/* Step 3 — Вопрос */}
            <div className={`ds-chat__pane${step === 3 ? ' is-active' : ''}`}>
              <div className="ds-chat__field">
                <label htmlFor="nc-question">Ваш вопрос по этим источникам</label>
                <textarea
                  id="nc-question"
                  placeholder="Напр.: что обсуждали про доставку из Китая и какие цены называли?"
                  value={question}
                  rows={4}
                  onChange={(e) => setQuestion(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="ds-chat__modal-foot">
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={handleBack}
              style={{ visibility: step > 1 ? 'visible' : 'hidden' }}
            >
              Назад
            </button>
            <span className="spacer" />
            <LiquidButton
              className="btn btn--primary btn--sm"
              disabled={!canProceed() || createMutation.isPending}
              onClick={handleNext}
              style={{
                opacity: canProceed() ? 1 : 0.45,
                pointerEvents: canProceed() ? undefined : 'none',
              }}
            >
              {createMutation.isPending
                ? 'Создаём…'
                : step < 3
                  ? 'Далее'
                  : 'Создать чат'}
            </LiquidButton>
          </div>
        </div>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// New scheduled task modal
// ---------------------------------------------------------------------------

interface NewTaskModalProps {
  isOpen: boolean
  onClose: () => void
  onCreated: (task: ScheduledTaskResponse) => void
}

function NewTaskModal({ isOpen, onClose, onCreated }: NewTaskModalProps) {
  const [step, setStep] = useState(1)
  const [prompt, setPrompt] = useState('Сделай краткое summary за период')
  const [sourceTab, setSourceTab] = useState<'monitored' | 'all'>('all')
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null)
  const [selectedChats, setSelectedChats] = useState<Map<string, TgChatDialog>>(new Map())
  const [scheduleType, setScheduleType] = useState<'daily' | 'weekly' | 'custom'>('daily')
  const [scheduleTime, setScheduleTime] = useState('10:00')
  const [intervalDays, setIntervalDays] = useState(1)
  const [createError, setCreateError] = useState('')

  const userbotsQuery = useQuery({
    queryKey: ['me', 'userbots'],
    queryFn: () => api.getUserbots(),
    enabled: isOpen,
  })
  const userbots: Userbot[] = userbotsQuery.data?.userbots ?? []

  const { snapshot, polling, startPolling, failPolling, elapsedSeconds, cancelPollingByUser } = useDialogPolling(isOpen, false)
  const [manualSourceInput, setManualSourceInput] = useState('')
  const [manualSourceMsg, setManualSourceMsg] = useState<{ text: string; error?: boolean } | null>(null)

  function handleAddManualSource() {
    const raw = manualSourceInput.trim()
    if (!raw) return

    const tokens = raw.split(/[\n,]+/).map((t) => t.trim()).filter(Boolean)
    if (tokens.length === 0) return

    const nextSelected = new Map(selectedChats)
    let addedCount = 0

    const targetSessionId = selectedSessionId ?? userbots[0]?.id ?? null
    const targetUserbot = userbots.find((u) => u.id === targetSessionId)

    const snapChats = snapshot?.chats ?? []
    const byUsername = new Map<string, TgChatDialog>()
    const byChatId = new Map<number, TgChatDialog>()
    for (const c of snapChats) {
      if (c.chat_id) byChatId.set(Number(c.chat_id), c)
      if (c.username) byUsername.set(c.username.toLowerCase().replace(/^@/, ''), c)
      for (const alias of ((c as any).usernames || [])) {
        if (alias) byUsername.set(String(alias).toLowerCase().replace(/^@/, ''), c)
      }
    }

    for (const token of tokens) {
      const parsed = parseTelegramSourceInput(token)
      let matchedChat: TgChatDialog | undefined
      if (parsed.chat_id && byChatId.has(parsed.chat_id)) {
        matchedChat = byChatId.get(parsed.chat_id)
      } else if (parsed.username && byUsername.has(parsed.username.toLowerCase())) {
        matchedChat = byUsername.get(parsed.username.toLowerCase())
      }

      let chatToAdd: TgChatDialog
      if (matchedChat) {
        const titleWithTopic = parsed.topic_id
          ? `${matchedChat.title} · тема #${parsed.topic_id}`
          : matchedChat.title
        chatToAdd = {
          ...matchedChat,
          title: titleWithTopic,
          topic_id: parsed.topic_id,
          raw_source: parsed.raw_source || token,
        }
      } else {
        const baseTitle = parsed.display_title || parsed.raw_source || token
        chatToAdd = {
          chat_id: parsed.chat_id ?? 0,
          title: baseTitle,
          username: parsed.username,
          chat_type: 'supergroup',
          folder_ids: [],
          monitored: false,
          session_id: targetSessionId,
          userbot_label: targetUserbot?.label ?? null,
          is_active_userbot: true,
          topic_id: parsed.topic_id,
          raw_source: parsed.raw_source || token,
        }
      }

      const key = dialogSelectionKey(chatToAdd)
      nextSelected.set(key, chatToAdd)
      addedCount++
    }

    setSelectedChats(nextSelected)
    setManualSourceInput('')
    setManualSourceMsg({
      text: addedCount === 1 ? 'Источник добавлен' : `Добавлено источников: ${addedCount}`,
      error: false,
    })
    setTimeout(() => setManualSourceMsg(null), 4000)
  }

  const refreshDialogSnapshot = useCallback(() => {
    void api.refreshDialogs(false).then((res) => {
      if (res.status === 'failed') {
        failPolling(res.error ?? 'no_userbot')
        return
      }
      startPolling()
    }).catch(() => {
      failPolling('error')
    })
  }, [failPolling, startPolling])

  useEffect(() => {
    if (!isOpen) return
    const status = snapshot?.status
    if (status === undefined || status === null) return
    if ((status === 'requested' || status === 'running') && !polling) {
      startPolling()
      return
    }
    if ((status === 'none' || status === 'stale') && !polling) {
      refreshDialogSnapshot()
    }
  }, [isOpen, snapshot?.status, polling, startPolling, refreshDialogSnapshot])

  useEffect(() => {
    if (!isOpen) return
    setStep(1)
    setPrompt('Сделай краткое summary за период')
    setSourceTab('all')
    setSelectedSessionId(null)
    setSelectedChats(new Map())
    setManualSourceInput('')
    setManualSourceMsg(null)
    setScheduleType('daily')
    setScheduleTime('10:00')
    setIntervalDays(1)
    setCreateError('')
  }, [isOpen])

  const createMutation = useMutation({
    mutationFn: (data: CreateScheduledTaskRequest) => api.createScheduledTask(data),
    onSuccess: (res) => {
      onCreated(res.task)
      onClose()
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 400) {
        setCreateError('Проверьте источники и расписание.')
      } else {
        setCreateError('Не удалось создать задачу.')
      }
    },
  })

  function handleSelectUserbot(sessionId: number | null) {
    setSelectedSessionId(sessionId)
    setSelectedChats(new Map())
  }

  function canProceed(): boolean {
    if (step === 1) return selectedChats.size > 0
    if (step === 2) return prompt.trim().length > 0
    return Boolean(scheduleTime) && (scheduleType !== 'custom' || intervalDays > 0)
  }

  function handleNext() {
    setCreateError('')
    if (step < 3) {
      setStep((s) => s + 1)
      return
    }
    const fallbackSessionId = selectedSessionId ?? Array.from(selectedChats.values())[0]?.session_id ?? userbots[0]?.id ?? null
    createMutation.mutate({
      prompt: prompt.trim(),
      group_ids: [],
      extra_chats: Array.from(selectedChats.values()).map((c) => ({
        chat_id: c.chat_id || 0,
        title: c.title,
        username: c.username,
        session_id: c.session_id ?? fallbackSessionId,
        topic_id: c.topic_id ?? null,
        raw_source: c.raw_source ?? null,
      })),
      schedule_type: scheduleType,
      schedule_time: scheduleTime,
      interval_days: scheduleType === 'custom' ? intervalDays : null,
      max_characters: 20000,
    })
  }

  function handleBack() {
    if (step > 1) setStep((s) => s - 1)
  }

  function handleScrimClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose()
  }

  if (!isOpen) return null

  const stepLabels = ['Источники', 'Задача', 'Расписание']
  const scheduleCards = [
    { key: 'daily' as const, title: 'Каждый день', caption: 'Бот пришлёт ответ один раз в день.' },
    { key: 'weekly' as const, title: 'Раз в неделю', caption: 'Бот пришлёт ответ по понедельникам.' },
    { key: 'custom' as const, title: 'Каждые N дней', caption: 'Задайте свой интервал.' },
  ]

  return (
    <>
      <div className="ds-chat__scrim is-open" onClick={onClose} />
      <div
        className="ds-chat__modal is-open"
        role="dialog"
        aria-modal
        aria-label="Создать задачу"
        onClick={handleScrimClick}
      >
        <div className="ds-chat__modal-card">
          <div className="ds-chat__modal-head">
            <h2>Создать задачу</h2>
            <button type="button" className="ds-chat__icon-btn" onClick={onClose} aria-label="Закрыть">
              {IconClose}
            </button>
          </div>

          <div className="ds-chat__steps" id="task-steps">
            {([1, 2, 3] as const).map((n, idx) => {
              const isActive = step === n
              const isDone = step > n
              return (
                <React.Fragment key={n}>
                  {idx > 0 && <span className="ds-chat__step__line" />}
                  <div className={`ds-chat__step${isActive ? ' is-active' : ''}${isDone ? ' is-done' : ''}`}>
                    <span className="n">{n}</span>
                    {stepLabels[idx]}
                  </div>
                </React.Fragment>
              )
            })}
          </div>

          <div className="ds-chat__modal-body">
            <div className={`ds-chat__pane${step === 1 ? ' is-active' : ''}`}>
              {/* Manual source input */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  padding: '10px 12px',
                  background: 'var(--surface-2, #f9fafb)',
                  borderRadius: 8,
                  border: '1px solid var(--line-2, #e5e7eb)',
                  marginBottom: 12,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>
                    Добавить чат, тему или канал вручную:
                  </span>
                  {manualSourceMsg && (
                    <span
                      style={{
                        fontSize: 12,
                        color: manualSourceMsg.error ? 'var(--danger, #b42318)' : 'var(--accent, #0284c7)',
                      }}
                    >
                      {manualSourceMsg.text}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    placeholder="Например: https://t.me/visadtv/4738, @channel или ID"
                    value={manualSourceInput}
                    onChange={(e) => setManualSourceInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleAddManualSource()
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '7px 10px',
                      fontSize: 13,
                      border: '1px solid var(--line-2, #d1d5db)',
                      borderRadius: 6,
                      background: '#fff',
                      color: 'var(--ink)',
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn--primary btn--sm"
                    disabled={!manualSourceInput.trim()}
                    onClick={handleAddManualSource}
                    style={{ flexShrink: 0 }}
                  >
                    Добавить
                  </button>
                </div>
              </div>

              {/* Selected custom / extra sources chips */}
              {selectedChats.size > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>
                      Выбрано источников: {selectedChats.size}
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedChats(new Map())}
                      style={{ background: 'transparent', border: 'none', color: 'var(--muted)', fontSize: 11, cursor: 'pointer' }}
                    >
                      Очистить все
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', maxHeight: 85, overflowY: 'auto' }}>
                    {Array.from(selectedChats.values()).map((chat) => {
                      const key = dialogSelectionKey(chat)
                      return (
                        <span
                          key={key}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '3px 8px',
                            borderRadius: 6,
                            background: 'var(--surface-3, #e0f2fe)',
                            color: 'var(--ink, #0369a1)',
                            fontSize: 12,
                            border: '1px solid var(--line, #bae6fd)',
                          }}
                        >
                          <span style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {chat.title}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const next = new Map(selectedChats)
                              next.delete(key)
                              setSelectedChats(next)
                            }}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              color: 'inherit',
                              padding: '0 2px',
                              fontSize: 13,
                              lineHeight: 1,
                            }}
                            title="Удалить источник"
                          >
                            ×
                          </button>
                        </span>
                      )
                    })}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                <div style={{ display: 'flex', gap: 0, border: '1px solid var(--line-2)', borderRadius: 8, overflow: 'hidden', width: 'fit-content', flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={() => setSourceTab('all')}
                    style={{
                      padding: '6px 14px',
                      fontSize: 13,
                      fontWeight: sourceTab === 'all' ? 600 : 400,
                      background: sourceTab === 'all' ? 'var(--accent)' : 'transparent',
                      color: sourceTab === 'all' ? '#fff' : 'var(--ink-2)',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    Все мои чаты
                  </button>
                  <button
                    type="button"
                    onClick={() => setSourceTab('monitored')}
                    style={{
                      padding: '6px 14px',
                      fontSize: 13,
                      fontWeight: sourceTab === 'monitored' ? 600 : 400,
                      background: sourceTab === 'monitored' ? 'var(--accent)' : 'transparent',
                      color: sourceTab === 'monitored' ? '#fff' : 'var(--ink-2)',
                      border: 'none',
                      borderLeft: '1px solid var(--line-2)',
                      cursor: 'pointer',
                    }}
                  >
                    Только источники проектов
                  </button>
                </div>

                {userbots.length > 1 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center', flex: 1, minWidth: 220 }}>
                    <span style={{ fontSize: 12, color: 'var(--muted)', marginRight: 2 }}>
                      Юзербот:
                    </span>
                    <button
                      type="button"
                      className={`ds-chat__preset${selectedSessionId === null ? ' is-on' : ''}`}
                      onClick={() => handleSelectUserbot(null)}
                    >
                      Все
                    </button>
                    {userbots.map((ub) => (
                      <button
                        key={ub.id}
                        type="button"
                        className={`ds-chat__preset${selectedSessionId === ub.id ? ' is-on' : ''}`}
                        onClick={() => handleSelectUserbot(ub.id)}
                      >
                        {ub.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <DialogPickerTab
                monitoredOnly={sourceTab === 'monitored'}
                snapshot={snapshot}
                polling={polling}
                elapsedSeconds={elapsedSeconds}
                onRefresh={() => {
                  setCreateError('')
                  refreshDialogSnapshot()
                }}
                onStopLoading={cancelPollingByUser}
                selectedChats={selectedChats}
                setSelectedChats={setSelectedChats}
                sessionFilter={selectedSessionId}
                onShowAll={() => setSourceTab('all')}
              />
              <div className="ds-chat__picker-foot" style={{ marginTop: 8 }}>
                <span className="sel">Выбрано: {selectedChats.size}</span>
              </div>
            </div>

            <div className={`ds-chat__pane${step === 2 ? ' is-active' : ''}`}>
              <div className="ds-chat__field">
                <label htmlFor="task-prompt">Что бот должен присылать?</label>
                <textarea
                  id="task-prompt"
                  value={prompt}
                  rows={4}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Напр.: Сделай краткое summary за период по выбранным чатам"
                />
              </div>
            </div>

            <div className={`ds-chat__pane${step === 3 ? ' is-active' : ''}`}>
              <div className="ds-chat__radio">
                {scheduleCards.map((card) => (
                  <div
                    key={card.key}
                    className={`ds-chat__radio-card${scheduleType === card.key ? ' is-on' : ''}`}
                    onClick={() => setScheduleType(card.key)}
                  >
                    <span className="ds-chat__radio-card__dot" />
                    <div>
                      <b>{card.title}</b>
                      <small>{card.caption}</small>
                      {card.key === 'custom' && (
                        <div className="ds-chat__radio-card__controls">
                          <span className="ds-chat__num">
                            дней:{' '}
                            <input
                              type="number"
                              value={intervalDays}
                              min={1}
                              max={30}
                              onClick={(e) => {
                                e.stopPropagation()
                                setScheduleType('custom')
                              }}
                              onChange={(e) => setIntervalDays(Number(e.target.value))}
                            />
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="ds-chat__field" style={{ marginBottom: 0 }}>
                <label htmlFor="task-time">Время отправки</label>
                <input
                  id="task-time"
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="ds-chat__modal-foot">
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={handleBack}
              style={{ visibility: step > 1 ? 'visible' : 'hidden' }}
            >
              Назад
            </button>
            {createError && (
              <span style={{ fontSize: 12, color: 'var(--danger-ink)', minWidth: 0 }}>
                {createError}
              </span>
            )}
            <span className="spacer" />
            <LiquidButton
              className="btn btn--primary btn--sm"
              disabled={!canProceed() || createMutation.isPending}
              onClick={handleNext}
              style={{
                opacity: canProceed() ? 1 : 0.45,
                pointerEvents: canProceed() ? undefined : 'none',
              }}
            >
              {createMutation.isPending
                ? 'Создаём…'
                : step < 3
                  ? 'Далее'
                  : 'Создать задачу'}
            </LiquidButton>
          </div>
        </div>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Task editor
// ---------------------------------------------------------------------------

interface TaskEditorProps {
  task: ScheduledTaskResponse
  isSaving: boolean
  error: string
  onSave: (taskId: number, data: UpdateScheduledTaskRequest) => void
}

function normalizedTaskScheduleType(task: ScheduledTaskResponse): 'daily' | 'weekly' | 'custom' {
  return task.schedule_type === 'weekly' || task.schedule_type === 'custom'
    ? task.schedule_type
    : 'daily'
}

function TaskEditor({ task, isSaving, error, onSave }: TaskEditorProps) {
  const [prompt, setPrompt] = useState(task.prompt)
  const [enabled, setEnabled] = useState(task.enabled)
  const [scheduleType, setScheduleType] = useState<'daily' | 'weekly' | 'custom'>(
    normalizedTaskScheduleType(task),
  )
  const [scheduleTime, setScheduleTime] = useState(task.schedule_time ?? '10:00')
  const [intervalDays, setIntervalDays] = useState(task.interval_days ?? 1)

  useEffect(() => {
    setPrompt(task.prompt)
    setEnabled(task.enabled)
    setScheduleType(normalizedTaskScheduleType(task))
    setScheduleTime(task.schedule_time ?? '10:00')
    setIntervalDays(task.interval_days ?? 1)
  }, [task])

  const canSave = prompt.trim().length > 0 && Boolean(scheduleTime) && (scheduleType !== 'custom' || intervalDays > 0)
  const scheduleCards = [
    { key: 'daily' as const, title: 'Каждый день', caption: 'Один ответ в день.' },
    { key: 'weekly' as const, title: 'Раз в неделю', caption: 'Ответ по понедельникам.' },
    { key: 'custom' as const, title: 'Каждые N дней', caption: 'Свой интервал.' },
  ]
  const sourceDetails = task.source_details?.length
    ? task.source_details
    : task.sources.map((title) => ({ title, url: null }))

  function handleSave() {
    if (!canSave) return
    onSave(task.id, {
      prompt: prompt.trim(),
      schedule_type: scheduleType,
      schedule_time: scheduleTime,
      interval_days: scheduleType === 'custom' ? intervalDays : null,
      enabled,
    })
  }

  return (
    <div className="ds-chat__task-editor">
      <div className="ds-chat__task-editor-card">
        <div className="ds-chat__task-editor-top">
          <div>
            <h2>Редактирование задачи</h2>
            <p>{task.sources.length} {pluralRu(task.sources.length, 'источник', 'источника', 'источников')} · {formatTaskSchedule(task)}</p>
          </div>
          <label className="ds-chat__toggle">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <span />
            {enabled ? 'Включена' : 'Выключена'}
          </label>
        </div>

        <div className="ds-chat__field">
          <label htmlFor={`task-prompt-${task.id}`}>Что бот должен присылать?</label>
          <textarea
            id={`task-prompt-${task.id}`}
            value={prompt}
            rows={5}
            onChange={(e) => setPrompt(e.target.value)}
          />
        </div>

        <div className="ds-chat__task-grid">
          <div className="ds-chat__radio ds-chat__radio--compact">
            {scheduleCards.map((card) => (
              <div
                key={card.key}
                className={`ds-chat__radio-card${scheduleType === card.key ? ' is-on' : ''}`}
                onClick={() => setScheduleType(card.key)}
              >
                <span className="ds-chat__radio-card__dot" />
                <div>
                  <b>{card.title}</b>
                  <small>{card.caption}</small>
                  {card.key === 'custom' && (
                    <div className="ds-chat__radio-card__controls">
                      <span className="ds-chat__num">
                        дней:{' '}
                        <input
                          type="number"
                          value={intervalDays}
                          min={1}
                          max={30}
                          onClick={(e) => {
                            e.stopPropagation()
                            setScheduleType('custom')
                          }}
                          onChange={(e) => setIntervalDays(Number(e.target.value))}
                        />
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="ds-chat__field ds-chat__task-time-field">
            <label htmlFor={`task-time-${task.id}`}>Время отправки</label>
            <input
              id={`task-time-${task.id}`}
              type="time"
              value={scheduleTime}
              onChange={(e) => setScheduleTime(e.target.value)}
            />
          </div>
        </div>

        <div className="ds-chat__task-sources">
          <div className="ds-chat__ctx-label">Источники задачи</div>
          <div className="ds-chat__task-sources-list">
            {sourceDetails.map((source, idx) => (
              source.url ? (
                <a
                  key={`${task.id}-${idx}`}
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Открыть источник в Telegram"
                >
                  {source.title}
                </a>
              ) : (
                <span key={`${task.id}-${idx}`}>{source.title}</span>
              )
            ))}
          </div>
        </div>

        {task.last_execution?.digest_content && (
          <div className="ds-chat__task-result">
            <div className="ds-chat__ctx-label">
              Последний результат · {new Date(task.last_execution.executed_at).toLocaleString('ru-RU')}
            </div>
            <div className="ds-chat__answer">
              <AssistantContent
                text={task.last_execution.digest_content}
                sourceTitles={task.sources}
              />
            </div>
          </div>
        )}

        <div className="ds-chat__task-editor-actions">
          {error && <span className="ds-chat__task-error">{error}</span>}
          <LiquidButton
            className="btn btn--primary btn--sm"
            disabled={!canSave || isSaving}
            onClick={handleSave}
            style={{
              opacity: canSave ? 1 : 0.45,
              pointerEvents: canSave ? undefined : 'none',
            }}
          >
            {isSaving ? 'Сохраняю…' : 'Сохранить'}
          </LiquidButton>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Context rail
// ---------------------------------------------------------------------------

interface ContextRailProps {
  session: ChatSessionDetail | null
  onToggle: () => void
}

function ContextRail({ session, onToggle }: ContextRailProps) {
  // Prefer source_results when available (new sessions); fall back to source_titles
  const useSourceResults =
    session !== null &&
    Array.isArray(session.source_results) &&
    session.source_results.length > 0

  const sourceCount = useSourceResults
    ? session!.source_results.length
    : (session?.source_titles.length ?? 0)

  return (
    <aside className="ds-chat__rail ds-chat__context-rail">
      <div className="ds-chat__rail-head">
        {/* Label removed — the session context is already implied by the chat. */}
        <span />
        <button
          type="button"
          className="ds-chat__icon-btn"
          onClick={onToggle}
          aria-label="Свернуть контекст"
        >
          {IconChevronRight}
        </button>
      </div>
      <div className="ds-chat__ctx" id="ctx-body">
        {session ? (
          <>
            <div className="ds-chat__ctx-block">
              <div className="ds-chat__ctx-label">
                Источники сессии · {sourceCount}
              </div>
              {useSourceResults
                ? session!.source_results.map((sr, i) => {
                    const title = sr.title ?? `Источник ${i + 1}`
                    const noAccess = sr.count === 0
                    return (
                      <div key={i} className="ds-chat__src is-on" style={{ cursor: 'default' }}>
                        <span className="ds-chat__check">{IconCheck}</span>
                        <span className="ds-chat__src__avatar">{srcInitials(title)}</span>
                        <span className="ds-chat__src__meta">
                          <b>{title}</b>
                          {noAccess ? (
                            <small style={{ color: 'var(--danger-ink, oklch(0.5 0.18 25))', fontWeight: 500 }}>
                              нет доступа
                            </small>
                          ) : (
                            <small style={{ color: 'var(--muted)' }}>
                              {sr.count} {pluralRu(sr.count, 'сообщение', 'сообщения', 'сообщений')}
                            </small>
                          )}
                        </span>
                      </div>
                    )
                  })
                : session.source_titles.map((title, i) => (
                    <div key={i} className="ds-chat__src is-on" style={{ cursor: 'default' }}>
                      <span className="ds-chat__check">{IconCheck}</span>
                      <span className="ds-chat__src__avatar">{srcInitials(title)}</span>
                      <span className="ds-chat__src__meta">
                        <b>{title}</b>
                      </span>
                    </div>
                  ))}
            </div>
          </>
        ) : (
          <div
            style={{
              padding: '24px 14px',
              fontSize: 13,
              color: 'var(--muted)',
              textAlign: 'center',
            }}
          >
            Выберите сессию
          </div>
        )}
      </div>
    </aside>
  )
}

// ---------------------------------------------------------------------------
// ChatPage
// ---------------------------------------------------------------------------

type ChatState = 'empty' | 'loading' | 'thread' | 'fault'

// Both in-progress ingest statuses (queued + worker active)
const INGEST_STATES = ['ingesting', 'ingesting_run'] as const
type IngestState = (typeof INGEST_STATES)[number]
function isIngesting(status: string): status is IngestState {
  return (INGEST_STATES as readonly string[]).includes(status)
}

type DeleteConfirmTarget =
  | { kind: 'one'; id: number; title: string }
  | { kind: 'all'; count: number }

interface DeleteConfirmDialogProps {
  target: DeleteConfirmTarget | null
  isBusy: boolean
  onCancel: () => void
  onConfirm: () => void
}

function DeleteConfirmDialog({
  target,
  isBusy,
  onCancel,
  onConfirm,
}: DeleteConfirmDialogProps) {
  if (!target) return null

  const isAll = target.kind === 'all'
  const title = isAll ? 'Удалить все чаты?' : 'Удалить чат?'
  const description = isAll
    ? `Будут удалены ${target.count} ${pluralRu(target.count, 'чат', 'чата', 'чатов')} вместе с историей сообщений и собранным контекстом.`
    : `Чат «${target.title}» будет удален вместе с историей сообщений и собранным контекстом.`

  return (
    <>
      <div className="ds-chat__scrim ds-chat__confirm-scrim is-open" onClick={onCancel} />
      <div
        className="ds-chat__confirm is-open"
        role="dialog"
        aria-modal
        aria-label={title}
      >
        <div className="ds-chat__confirm-card">
          <div className="ds-chat__confirm-icon">{IconWarnTriangle}</div>
          <div className="ds-chat__confirm-copy">
            <h2>{title}</h2>
            <p>{description}</p>
            <small>Действие необратимо.</small>
          </div>
          <div className="ds-chat__confirm-actions">
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={isBusy}
              onClick={onCancel}
            >
              Отмена
            </button>
            <button
              type="button"
              className="ds-chat__danger-btn"
              disabled={isBusy}
              onClick={onConfirm}
            >
              {IconTrash}
              {isBusy ? 'Удаляю…' : 'Удалить'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export function ChatPage() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  // Rail collapse state (desktop) / drawer state (mobile)
  const [leftClosed, setLeftClosed] = useState(false)
  const [rightClosed, setRightClosed] = useState(false)
  const [leftOpen, setLeftOpen] = useState(false) // mobile drawer
  const [rightOpen, setRightOpen] = useState(false) // tablet drawer

  // ------- Resizable rails (desktop) -------
  const LEFT_MIN = 200, LEFT_MAX = 480, RIGHT_MIN = 240, RIGHT_MAX = 560
  const chatRef = useRef<HTMLElement>(null)
  const [leftW, setLeftW] = useState<number>(() => {
    const v = Number(localStorage.getItem('ds-chat-left-w'))
    return v >= LEFT_MIN && v <= LEFT_MAX ? v : 276
  })
  const [rightW, setRightW] = useState<number>(() => {
    const v = Number(localStorage.getItem('ds-chat-right-w'))
    return v >= RIGHT_MIN && v <= RIGHT_MAX ? v : 332
  })
  const [draggingRail, setDraggingRail] = useState<'left' | 'right' | null>(null)

  function startRailDrag(side: 'left' | 'right', e: React.PointerEvent) {
    e.preventDefault()
    setDraggingRail(side)
    const move = (ev: PointerEvent) => {
      const rect = chatRef.current?.getBoundingClientRect()
      if (!rect) return
      if (side === 'left') {
        const w = Math.min(LEFT_MAX, Math.max(LEFT_MIN, ev.clientX - rect.left))
        setLeftW(w)
      } else {
        const w = Math.min(RIGHT_MAX, Math.max(RIGHT_MIN, rect.right - ev.clientX))
        setRightW(w)
      }
    }
    const up = () => {
      setDraggingRail(null)
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      // persist latest values
      setLeftW((v) => { localStorage.setItem('ds-chat-left-w', String(v)); return v })
      setRightW((v) => { localStorage.setItem('ds-chat-right-w', String(v)); return v })
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  const [activeSessionId, setActiveSessionId] = useState<number | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [taskModalOpen, setTaskModalOpen] = useState(false)
  const [taskNotice, setTaskNotice] = useState('')
  const [activeTaskId, setActiveTaskId] = useState<number | null>(null)
  const [taskEditError, setTaskEditError] = useState('')
  const [railMode, setRailMode] = useState<'sessions' | 'tasks'>('sessions')
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmTarget | null>(null)
  const [inputValue, setInputValue] = useState('')
  const [isWaiting, setIsWaiting] = useState(false)
  // Optimistic messages appended before API responds
  const [pendingUserMsg, setPendingUserMsg] = useState<string | null>(null)
  // When a question fails, keep the message visible + show retry UI
  const [failedQuestion, setFailedQuestion] = useState<string | null>(null)

  // Auto-ask: track session id → question for newly created sessions
  const pendingAutoAskRef = useRef<Map<number, string>>(new Map())
  // Guard against double-firing auto-ask across re-renders
  const autoAskedSessionsRef = useRef<Set<number>>(new Set())

  const threadRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // ------- Media queries (replicate JS from chat.js) -------
  const mqTablet = typeof window !== 'undefined' ? window.matchMedia('(max-width: 1240px)') : null
  const mqMobile = typeof window !== 'undefined' ? window.matchMedia('(max-width: 900px)') : null

  function toggleLeft() {
    if (mqMobile?.matches) {
      setLeftOpen((v) => !v)
      setRightOpen(false)
    } else {
      setLeftClosed((v) => !v)
    }
  }

  function toggleRight() {
    if (mqTablet?.matches) {
      setRightOpen((v) => !v)
      setLeftOpen(false)
    } else {
      setRightClosed((v) => !v)
    }
  }

  function closeDrawers() {
    setLeftOpen(false)
    setRightOpen(false)
  }

  // ------- Me query (for timezone) -------
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => api.getMe(),
  })
  const userTimezone = meQuery.data?.timezone ?? 'UTC'

  // ------- Sessions query (polls while any is ingesting) -------
  const sessionsQuery = useQuery({
    queryKey: ['chat', 'sessions'],
    queryFn: () => api.getChatSessions(),
    refetchInterval: (query) => {
      const sessions = query.state.data?.sessions ?? []
      return sessions.some((s) => isIngesting(s.status)) ? 2000 : false
    },
  })

  const [deletedSessionIds, setDeletedSessionIds] = useState<Set<number>>(() => new Set())
  const sessions = useMemo(() => {
    const rawSessions = sessionsQuery.data?.sessions ?? []
    if (deletedSessionIds.size === 0) return rawSessions
    return rawSessions.filter((s) => !deletedSessionIds.has(s.id))
  }, [deletedSessionIds, sessionsQuery.data?.sessions])

  const tasksQuery = useQuery({
    queryKey: ['chat', 'tasks'],
    queryFn: () => api.getScheduledTasks(),
  })
  const tasks = tasksQuery.data?.tasks ?? []
  const visibleSessionsCount = sessions.length
  const visibleTasksCount = tasks.length

  useEffect(() => {
    if (railMode !== 'tasks') return
    if (tasks.length === 0) {
      setActiveTaskId(null)
      return
    }
    if (activeTaskId === null || !tasks.some((task) => task.id === activeTaskId)) {
      setActiveTaskId(tasks[0].id)
    }
  }, [activeTaskId, railMode, tasks])

  // Sorting of the sessions list (chats are org-wide — all userbots — regardless
  // of which userbot you logged in with).
  const [sortMode, setSortMode] = useState<'recent' | 'old' | 'userbot'>('recent')
  const sortedSessions = useMemo(() => {
    const arr = [...sessions]
    if (sortMode === 'userbot') {
      arr.sort((a, b) => {
        const la = a.userbot_label ?? 'я'
        const lb = b.userbot_label ?? 'я'
        if (la !== lb) return la.localeCompare(lb)
        return new Date(b.last_activity).getTime() - new Date(a.last_activity).getTime()
      })
    } else {
      arr.sort((a, b) => {
        const ta = new Date(a.last_activity).getTime()
        const tb = new Date(b.last_activity).getTime()
        return sortMode === 'old' ? ta - tb : tb - ta
      })
    }
    return arr
  }, [sessions, sortMode])

  const cancelIngestMutation = useMutation({
    mutationFn: (id: number) => api.cancelChatSession(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['chat', 'sessions'] })
    },
  })

  const deleteSessionMutation = useMutation({
    mutationFn: (id: number) => api.deleteChatSession(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['chat', 'sessions'] })
      setDeletedSessionIds((prev) => {
        const next = new Set(prev)
        next.add(id)
        return next
      })
      queryClient.setQueryData<{ sessions: ChatSession[] } | undefined>(
        ['chat', 'sessions'],
        (old) => old ? { sessions: old.sessions.filter((s) => s.id !== id) } : old,
      )
      setActiveSessionId((cur) => (cur === id ? null : cur))
      void queryClient.removeQueries({ queryKey: ['chat', 'messages', id] })
    },
    onSuccess: (_d, id) => {
      void queryClient.invalidateQueries({ queryKey: ['chat', 'sessions'] })
      setActiveSessionId((cur) => (cur === id ? null : cur))
    },
    onError: (_error, id) => {
      setDeletedSessionIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      void queryClient.invalidateQueries({ queryKey: ['chat', 'sessions'] })
    },
  })
  const deleteAllMutation = useMutation({
    mutationFn: () => api.deleteAllChatSessions(),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['chat', 'sessions'] })
      queryClient.setQueryData<{ sessions: ChatSession[] } | undefined>(
        ['chat', 'sessions'],
        (old) => old ? { sessions: [] } : old,
      )
      setActiveSessionId(null)
      void queryClient.removeQueries({ queryKey: ['chat', 'messages'] })
    },
    onSuccess: () => {
      setDeletedSessionIds(() => new Set())
      void queryClient.invalidateQueries({ queryKey: ['chat', 'sessions'] })
      setActiveSessionId(null)
    },
    onError: () => {
      setDeletedSessionIds(() => new Set())
      void queryClient.invalidateQueries({ queryKey: ['chat', 'sessions'] })
    },
  })

  function handleDeleteSession(e: React.MouseEvent, id: number, title: string) {
    e.stopPropagation()
    if (deleteSessionMutation.isPending) return
    setDeleteConfirm({ kind: 'one', id, title })
  }
  function handleDeleteAll() {
    if (deleteAllMutation.isPending || sessions.length === 0) return
    setDeleteConfirm({ kind: 'all', count: sessions.length })
  }
  function confirmDelete() {
    const target = deleteConfirm
    if (!target) return
    setDeleteConfirm(null)
    if (target.kind === 'one') {
      deleteSessionMutation.mutate(target.id)
    } else {
      deleteAllMutation.mutate()
    }
  }

  const refreshSessionMutation = useMutation({
    mutationFn: (id: number) => api.refreshChatSession(id),
    onSuccess: (_session, id) => {
      void queryClient.invalidateQueries({ queryKey: ['chat', 'sessions'] })
      void queryClient.invalidateQueries({ queryKey: ['chat', 'messages', id] })
    },
  })

  function handleRefreshSession() {
    if (!activeSessionId || refreshSessionMutation.isPending) return
    const activeSession = sessions.find((s) => s.id === activeSessionId)
    if (!activeSession || isIngesting(activeSession.status)) return
    refreshSessionMutation.mutate(activeSessionId)
  }

  const userbotsQuery = useQuery({
    queryKey: ['me', 'userbots'],
    queryFn: () => api.getUserbots(),
  })
  const activeUserbotId = userbotsQuery.data?.userbots.find((u) => u.is_active)?.id ?? null

  // Auto-select first ready session when list loads
  useEffect(() => {
    if (activeSessionId === null && sessions.length > 0) {
      const first = sessions[0]
      setActiveSessionId(first.id)
    }
  }, [sessions, activeSessionId])

  // Auto-select newly ingested session (transitions ingesting → ready)
  useEffect(() => {
    if (activeSessionId !== null) {
      const active = sessions.find((s) => s.id === activeSessionId)
      if (active?.status === 'active') {
        // Trigger message re-fetch when session becomes ready
        void queryClient.invalidateQueries({ queryKey: ['chat', 'messages', activeSessionId] })
      }
    }
  }, [sessions, activeSessionId, queryClient])

  // Auto-ask: when a session created via the wizard transitions to 'active'
  // and has 0 messages, fire its stored question exactly once.
  useEffect(() => {
    for (const s of sessions) {
      const pendingQ = pendingAutoAskRef.current.get(s.id)
      if (!pendingQ) continue
      if (s.status !== 'active') continue
      if (autoAskedSessionsRef.current.has(s.id)) continue
      // Check messages count from the session list object
      if (s.message_count > 0) {
        // Already has messages — don't auto-ask, just clean up
        pendingAutoAskRef.current.delete(s.id)
        autoAskedSessionsRef.current.add(s.id)
        continue
      }
      // Mark as auto-asked before firing to prevent double-fire
      autoAskedSessionsRef.current.add(s.id)
      pendingAutoAskRef.current.delete(s.id)
      fireAsk(s.id, pendingQ)
    }
  }, [sessions])

  // ------- Messages query -------
  const messagesQuery = useQuery({
    queryKey: ['chat', 'messages', activeSessionId],
    queryFn: () => api.getChatMessages(activeSessionId!),
    enabled: activeSessionId !== null,
  })

  const sessionDetail: ChatSessionDetail | null = messagesQuery.data?.session ?? null
  const messages: ChatMessage[] = messagesQuery.data?.messages ?? []

  // ------- Ask mutation -------
  const askMutation = useMutation({
    mutationFn: ({ sessionId, question }: { sessionId: number; question: string }) =>
      api.askChat(sessionId, question),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['chat', 'messages', activeSessionId] })
      setPendingUserMsg(null)
      setFailedQuestion(null)
      setIsWaiting(false)
    },
    onError: (_err, variables) => {
      // Keep the question visible as a failed message with retry affordance
      setFailedQuestion(variables.question)
      setPendingUserMsg(null)
      setIsWaiting(false)
    },
  })

  // ------- Scroll thread to bottom -------
  useEffect(() => {
    if (threadRef.current) {
      requestAnimationFrame(() => {
        if (threadRef.current) {
          threadRef.current.scrollTop = threadRef.current.scrollHeight
        }
      })
    }
  }, [messages, pendingUserMsg, isWaiting])

  // ------- Auto-grow textarea -------
  function autoGrow() {
    const ta = textareaRef.current
    if (!ta) return
    ta.style.height = 'auto'
    ta.style.height = `${Math.min(ta.scrollHeight, 140)}px`
  }

  // ------- Send message -------
  function handleSend() {
    const q = inputValue.trim()
    if (!q || !activeSessionId || isWaiting) return
    const activeSession = sessions.find((s) => s.id === activeSessionId)
    if (!activeSession || activeSession.status !== 'active') return

    setFailedQuestion(null)
    setPendingUserMsg(q)
    setIsWaiting(true)
    setInputValue('')
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
    askMutation.mutate({ sessionId: activeSessionId, question: q })
  }

  // ------- Retry failed question -------
  function handleRetry() {
    if (!failedQuestion || !activeSessionId || isWaiting) return
    const activeSession = sessions.find((s) => s.id === activeSessionId)
    if (!activeSession || activeSession.status !== 'active') return

    const q = failedQuestion
    setFailedQuestion(null)
    setPendingUserMsg(q)
    setIsWaiting(true)
    askMutation.mutate({ sessionId: activeSessionId, question: q })
  }

  // ------- Internal ask (used by auto-ask) -------
  function fireAsk(sessionId: number, question: string) {
    setActiveSessionId(sessionId)
    setFailedQuestion(null)
    setPendingUserMsg(question)
    setIsWaiting(true)
    askMutation.mutate({ sessionId, question })
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // ------- Determine chat state -------
  const activeSession = sessions.find((s) => s.id === activeSessionId)
  const activeTask = tasks.find((task) => task.id === activeTaskId) ?? null
  const taskMode = railMode === 'tasks' && activeTask !== null
  let chatState: ChatState = 'empty'
  if (sessions.length === 0 && !sessionsQuery.isLoading) {
    chatState = 'empty'
  } else if (activeSession !== undefined && isIngesting(activeSession.status)) {
    chatState = 'loading'
  } else if (activeSession !== undefined && activeSession.status === 'failed') {
    chatState = 'fault'
  } else if (activeSessionId !== null) {
    chatState = 'thread'
  }

  // ------- CSS classes for the grid -------
  const dsChatClasses = [
    'ds-chat',
    leftClosed ? 'is-left-closed' : '',
    rightClosed ? 'is-right-closed' : '',
    leftOpen ? 'is-left-open' : '',
    rightOpen ? 'is-right-open' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const drawerScrimOpen = leftOpen || rightOpen

  const updateTaskMutation = useMutation({
    mutationFn: ({ taskId, data }: { taskId: number; data: UpdateScheduledTaskRequest }) =>
      api.updateScheduledTask(taskId, data),
    onSuccess: (res) => {
      setTaskEditError('')
      setActiveTaskId(res.task.id)
      void queryClient.invalidateQueries({ queryKey: ['chat', 'tasks'] })
      setTaskNotice(`Задача обновлена: ${formatTaskSchedule(res.task)}`)
      window.setTimeout(() => setTaskNotice(''), 5000)
    },
    onError: () => {
      setTaskEditError('Не удалось сохранить задачу.')
    },
  })

  // ------- Handle session created -------
  const handleSessionCreated = useCallback(
    (session: ChatSession, question: string) => {
      setActiveSessionId(session.id)
      setActiveTaskId(null)
      setRailMode('sessions')
      setModalOpen(false)
      // Register for auto-ask when session becomes active
      if (question) {
        pendingAutoAskRef.current.set(session.id, question)
      }
    },
    [],
  )

  const handleTaskCreated = useCallback((task: ScheduledTaskResponse) => {
    void queryClient.invalidateQueries({ queryKey: ['chat', 'tasks'] })
    setRailMode('tasks')
    setActiveTaskId(task.id)
    setTaskEditError('')
    setTaskNotice(`Задача создана: ${formatTaskSchedule(task)}`)
    window.setTimeout(() => setTaskNotice(''), 5000)
  }, [queryClient])

  const centerState = taskMode ? 'task' : (chatState === 'fault' ? 'expired' : chatState)

  return (
    <div className="ds-chat-fullbleed">
      <section
        ref={chatRef}
        className={dsChatClasses}
        id="ds-chat"
        data-state={centerState}
        style={{ ['--rail-left-w' as string]: `${leftW}px`, ['--rail-right-w' as string]: `${rightW}px` }}
      >
        {/* Draggable rail resizers (desktop). Hidden when the rail is collapsed. */}
        {!leftClosed && (
          <div
            className={`ds-chat__resizer ds-chat__resizer--left${draggingRail === 'left' ? ' is-dragging' : ''}`}
            onPointerDown={(e) => startRailDrag('left', e)}
            role="separator"
            aria-orientation="vertical"
            aria-label="Изменить ширину списка чатов"
          />
        )}
        {!rightClosed && (
          <div
            className={`ds-chat__resizer ds-chat__resizer--right${draggingRail === 'right' ? ' is-dragging' : ''}`}
            onPointerDown={(e) => startRailDrag('right', e)}
            role="separator"
            aria-orientation="vertical"
            aria-label="Изменить ширину панели источников"
          />
        )}

        {/* ============================================================
            LEFT RAIL — sessions list
            ============================================================ */}
        <aside className="ds-chat__rail ds-chat__sessions-rail">
          <div className="ds-chat__rail-head">
            <h2>Чаты по&nbsp;источникам</h2>
            <button
              type="button"
              className="ds-chat__icon-btn"
              onClick={toggleLeft}
              aria-label="Свернуть список"
            >
              {IconChevronLeft}
            </button>
          </div>

          <div className="ds-chat__actions">
            <button
              type="button"
              className="ds-chat__new ds-chat__new--ghost"
              onClick={() => setModalOpen(true)}
              title="Новый чат"
            >
              {IconPlus}
              Новый чат
            </button>
            <button
              type="button"
              className="ds-chat__new ds-chat__new--ghost"
              onClick={() => setTaskModalOpen(true)}
              title="Создать задачу"
            >
              {IconPlus}
              Задача
            </button>
            {taskNotice && (
              <div className="ds-chat__task-notice" role="status">
                <span className="ds-chat__task-notice__icon">{IconCheck}</span>
                <span>{taskNotice}</span>
              </div>
            )}
          </div>

          {railMode === 'sessions' && sessions.length > 0 && (
            <div className="ds-chat__sessions-tools">
              <Select
                style={{ flex: '1 1 auto' }}
                ariaLabel="Сортировка"
                value={sortMode}
                onChange={(v) => setSortMode(v as 'recent' | 'old' | 'userbot')}
                options={[
                  { value: 'recent', label: 'Сначала недавние' },
                  { value: 'old', label: 'Сначала старые' },
                  { value: 'userbot', label: 'По юзерботу' },
                ]}
              />
              <button
                type="button"
                onClick={handleDeleteAll}
                disabled={deleteAllMutation.isPending}
                title="Удалить все чаты"
                className="ds-chat__bulk-delete"
              >
                {IconTrash}
                <span>{deleteAllMutation.isPending ? '…' : 'Все'}</span>
              </button>
            </div>
          )}

          <div className="ds-chat__rail-tabs" role="tablist" aria-label="Тип списка">
            <button
              type="button"
              className={`ds-chat__rail-tab${railMode === 'sessions' ? ' is-on' : ''}`}
              onClick={() => {
                setRailMode('sessions')
                setTaskEditError('')
              }}
            >
              Сессии
              <span key={`sessions-count-${visibleSessionsCount}`}>{visibleSessionsCount}</span>
            </button>
            <button
              type="button"
              className={`ds-chat__rail-tab${railMode === 'tasks' ? ' is-on' : ''}`}
              onClick={() => {
                setRailMode('tasks')
                if (activeTaskId === null && tasks.length > 0) setActiveTaskId(tasks[0].id)
              }}
            >
              Задачи
              <span key={`tasks-count-${visibleTasksCount}`}>{visibleTasksCount}</span>
            </button>
          </div>

          <div className="ds-chat__sessions" id="sessions-list">
            {railMode === 'sessions' && sessionsQuery.isLoading && (
              <div style={{ padding: '16px 12px', fontSize: 13, color: 'var(--muted)' }}>
                Загрузка…
              </div>
            )}
            {railMode === 'sessions' && sessions.length > 0 && (
              <div className="ds-chat__sessions-group">Сессии</div>
            )}
            {railMode === 'sessions' && sortedSessions.map((s) => {
              const isActive = s.id === activeSessionId
              const sessionIngesting = isIngesting(s.status)
              const dotCls = sessionIngesting
                ? 'ds-chat__session__dot--collecting'
                : 'ds-chat__session__dot--ready'
              const meta = sessionIngesting
                ? 'собираю сообщения…'
                : `${s.source_count} ${pluralRu(s.source_count, 'источник', 'источника', 'источников')} · ${relativeActivity(s.last_activity)}${s.userbot_label ? ` · ${s.userbot_label}` : ''}`

              return (
                <div
                  key={s.id}
                  className={`ds-chat__session${isActive ? ' is-active' : ''}`}
                  onClick={() => {
                    setActiveSessionId(s.id)
                    setActiveTaskId(null)
                    setTaskEditError('')
                    if (s.userbot_session_id && s.userbot_session_id !== activeUserbotId) {
                      void api.selectUserbot(s.userbot_session_id)
                        .then(() => {
                          void queryClient.invalidateQueries({ queryKey: ['me', 'userbots'] })
                        })
                        .catch(() => {/* keep opening the chat even if switching fails */})
                    }
                    closeDrawers()
                  }}
                >
                  <div className="ds-chat__session__name">
                    <span className={`ds-chat__session__dot ${dotCls}`} />
                    {s.title}
                  </div>
                  <div className="ds-chat__session__meta">{meta}</div>
                  <button
                    type="button"
                    onClick={(e) => handleDeleteSession(e, s.id, s.title)}
                    title="Удалить чат"
                    aria-label="Удалить чат"
                    className="ds-chat__session-delete"
                  >
                    {IconTrash}
                  </button>
                </div>
              )
            })}
            {railMode === 'tasks' && tasksQuery.isLoading && (
              <div style={{ padding: '16px 12px', fontSize: 13, color: 'var(--muted)' }}>
                Загрузка задач…
              </div>
            )}
            {railMode === 'tasks' && !tasksQuery.isLoading && tasks.length === 0 && (
              <div className="ds-chat__tasks-empty">
                Задач пока нет
              </div>
            )}
            {railMode === 'tasks' && tasks.length > 0 && (
              <div className="ds-chat__sessions-group">Мои задачи</div>
            )}
            {railMode === 'tasks' && tasks.map((task) => (
              <div
                key={task.id}
                className={`ds-chat__task-item${task.id === activeTaskId ? ' is-active' : ''}`}
                onClick={() => {
                  setActiveTaskId(task.id)
                  setTaskEditError('')
                  closeDrawers()
                }}
              >
                <div className="ds-chat__task-item__head">
                  <span className={`ds-chat__session__dot ${task.enabled ? 'ds-chat__session__dot--ready' : ''}`} />
                  <b>{task.prompt}</b>
                </div>
                <div className="ds-chat__task-item__meta">
                  {task.sources.length} {pluralRu(task.sources.length, 'источник', 'источника', 'источников')} · {formatTaskSchedule(task)}
                </div>
                {task.sources.length > 0 && (
                  <div className="ds-chat__task-item__sources">
                    {task.sources.slice(0, 2).join(', ')}
                    {task.sources.length > 2 ? ` +${task.sources.length - 2}` : ''}
                  </div>
                )}
              </div>
            ))}
          </div>
        </aside>

        {/* ============================================================
            CENTER — thread / empty / loading
            ============================================================ */}
        <section className="ds-chat__main">
          {/* Thread header */}
          <div className="ds-chat__thread-head">
            <button
              type="button"
              className="ds-chat__rail-reopen ds-chat__rail-reopen--left"
              onClick={toggleLeft}
              aria-label="Показать чаты"
            >
              {IconHamburger}
            </button>

            <div className="ds-chat__thread-head__title">
              <b id="chat-head-title">
                {taskMode ? 'Задача' : (activeSession?.title ?? 'Сессия')}
              </b>
              <small id="chat-head-sub">
                {taskMode && activeTask
                  ? `${activeTask.sources.length} ${pluralRu(activeTask.sources.length, 'источник', 'источника', 'источников')} · ${formatTaskSchedule(activeTask)}`
                  : activeSession
                  ? `${activeSession.source_count} ${pluralRu(activeSession.source_count, 'источник', 'источника', 'источников')} · ${activeSession.message_count} ${pluralRu(activeSession.message_count, 'сообщение', 'сообщения', 'сообщений')}`
                  : ''}
              </small>
            </div>

            <div className="ds-chat__thread-head__actions">
              {!taskMode && activeSession && isIngesting(activeSession.status) && (
                <button
                  type="button"
                  className="btn btn--danger btn--sm"
                  style={{ fontSize: 12, padding: '4px 10px', height: 28 }}
                  disabled={cancelIngestMutation.isPending}
                  onClick={() => cancelIngestMutation.mutate(activeSession.id)}
                  title="Остановить сбор сообщений"
                >
                  {cancelIngestMutation.isPending ? 'Останавливаю…' : 'Остановить выгрузку'}
                </button>
              )}
              {!taskMode && (
                <button
                  type="button"
                  className="ds-chat__refresh-context"
                  onClick={handleRefreshSession}
                  disabled={
                    !activeSession ||
                    isIngesting(activeSession.status) ||
                    refreshSessionMutation.isPending
                  }
                  aria-label="Обновить контекст"
                  title="Собрать новые сообщения в этот чат"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path d="M21 12a9 9 0 0 1-15.5 6.2M3 12A9 9 0 0 1 18.5 5.8" />
                    <path d="M21 4v6h-6M3 20v-6h6" />
                  </svg>
                  <span>{refreshSessionMutation.isPending ? 'Обновляю…' : 'Обновить контекст'}</span>
                </button>
              )}
              <button
                type="button"
                className="ds-chat__rail-reopen ds-chat__rail-reopen--right"
                onClick={toggleRight}
                aria-label="Показать контекст"
              >
                {IconPanel}
              </button>
            </div>
          </div>

          {taskMode && activeTask && (
            <TaskEditor
              task={activeTask}
              isSaving={updateTaskMutation.isPending}
              error={taskEditError}
              onSave={(taskId, data) => updateTaskMutation.mutate({ taskId, data })}
            />
          )}

          {/* Thread scroll region */}
          <div className="ds-chat__thread" ref={threadRef} id="chat-thread">
            <div className="ds-chat__thread-inner" id="chat-thread-inner">
              {messages.map((msg) =>
                msg.role === 'user' ? (
                  <UserBubble key={msg.id} content={msg.content} createdAt={msg.created_at} />
                ) : (
                  <AssistantBubble
                    key={msg.id}
                    msg={msg}
                    sourceTitles={sessionDetail?.source_titles ?? []}
                  />
                ),
              )}
              {pendingUserMsg && <UserBubble content={pendingUserMsg} />}
              {isWaiting && <TypingIndicator />}
              {failedQuestion && !isWaiting && (
                <>
                  <UserBubble content={failedQuestion} />
                  <div className="ds-chat__msg ds-chat__msg--ai">
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 14px',
                        borderRadius: 'var(--r-md)',
                        background: 'oklch(0.97 0.01 25)',
                        border: '1px solid oklch(0.88 0.04 25)',
                        fontSize: 13,
                        color: 'oklch(0.45 0.08 25)',
                        maxWidth: 480,
                      }}
                    >
                      <span style={{ flex: 1 }}>Не удалось получить ответ — попробуйте ещё раз</span>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={handleRetry}
                        style={{ flexShrink: 0, fontSize: 12 }}
                      >
                        Повторить
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Center states (empty / loading) */}
          <div className="ds-chat__center-state">
            {/* EMPTY */}
            <div className="ds-chat__empty">
              <div className="ds-chat__empty__ico">{IconChat}</div>
              <h2>Спросите свои Telegram-источники</h2>
              <p>
                SENSOR.Chat собирает сообщения из выбранных групп и отвечает на ваши
                вопросы — строго по содержимому и со ссылками на первоисточники.
              </p>
              <div className="ds-chat__empty__steps">
                <div className="ds-chat__empty__step">
                  <span className="n">1</span>
                  <span>
                    Выберите <b>источники</b> — мониторимые группы.
                  </span>
                </div>
                <div className="ds-chat__empty__step">
                  <span className="n">2</span>
                  <span>
                    Укажите <b>объём сбора</b> — N сообщений или период.
                  </span>
                </div>
                <div className="ds-chat__empty__step">
                  <span className="n">3</span>
                  <span>
                    Задавайте вопросы и получайте ответы <b>с цитатами</b>.
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => setModalOpen(true)}
                style={{ marginTop: 6 }}
              >
                {IconPlus}
                Создать первый чат
              </button>
            </div>

            {/* LOADING — rich ingest progress */}
            {(() => {
              const prog = activeSession?.ingest_progress ?? null
              const phase = prog?.phase ?? 'connecting'
              const groupsDone = prog?.groups_done ?? 0
              const groupsTotal = prog?.groups_total ?? 0
              const messages = prog?.messages ?? 0
              const sources = prog?.sources ?? []

              // Map backend phase → active checklist step index
              // connecting→0, collecting→1, indexing→3
              // Steps: 0=Подключение, 1=Сбор, 2=Дедупликация, 3=Индекс
              const activeStepIndex = phase === 'connecting' ? 0 : phase === 'collecting' ? 1 : 3

              function phaseClass(stepIdx: number): string {
                if (stepIdx < activeStepIndex) return 'is-done'
                if (stepIdx === activeStepIndex) return 'is-active'
                return ''
              }

              // Progress bar percent
              let rawPct: number
              if (groupsTotal > 0) {
                rawPct = Math.round((groupsDone / groupsTotal) * 100)
              } else {
                rawPct = phase === 'connecting' ? 8 : phase === 'indexing' ? 96 : 20
              }
              const pct = Math.min(100, Math.max(4, rawPct))

              return (
                <div className="ds-chat__loading ds-ingest" id="ds-ingest">
                  <div className="ds-ingest__head">
                    <div className="ds-chat__loading__spin" />
                    <div className="ds-ingest__headtext">
                      <h2>Собираю сообщения…</h2>
                      <p>Загружаю историю из выбранных групп и строю поисковый индекс. Обычно 1–10 минут.</p>
                    </div>
                  </div>
                  <div className="ds-ingest__counter">
                    <div className="ds-ingest__stat">
                      <span className="num">{messages}</span>
                      <span className="lbl">сообщений собрано</span>
                    </div>
                    <div className="ds-ingest__stat">
                      <span className="num">{groupsDone} / {groupsTotal}</span>
                      <span className="lbl">групп обработано</span>
                    </div>
                  </div>
                  <div className="ds-chat__loading__bar">
                    <i style={{ width: `${pct}%`, animation: 'none', marginLeft: 0 }} />
                  </div>
                  <ol className="ds-ingest__phases">
                    <li className={phaseClass(0)}><span className="tick" />Подключение к Telegram</li>
                    <li className={phaseClass(1)}><span className="tick" />Сбор сообщений по группам</li>
                    <li className={phaseClass(2)}><span className="tick" />Дедупликация и фильтрация</li>
                    <li className={phaseClass(3)}><span className="tick" />Построение поискового индекса</li>
                  </ol>
                  {sources.length > 0 && (
                    <div className="ds-ingest__sources">
                      {sources.map((s, i) => (
                        <div
                          key={i}
                          className={`ds-ingest__src${s.state === 'done' ? ' is-done' : s.state === 'active' ? ' is-active' : ''}`}
                        >
                          <span className="av">{initials(s.title)}</span>
                          <b>{s.title}</b>
                          <span className="cnt">{s.state === 'pending' ? '—' : s.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="ds-ingest__foot" style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
                    <span>Можно закрыть вкладку — сбор продолжится в фоне.</span>
                    <button
                      type="button"
                      className="btn btn--danger btn--sm"
                      disabled={cancelIngestMutation.isPending}
                      onClick={() => activeSession && cancelIngestMutation.mutate(activeSession.id)}
                      style={{ fontSize: 13, padding: '6px 16px' }}
                    >
                      {cancelIngestMutation.isPending ? 'Останавливаю…' : 'Остановить выгрузку'}
                    </button>
                  </div>
                </div>
              )
            })()}

            {/* FAULT — failed ingest */}
            {(() => {
              const err = activeSession?.ingest_error ?? null
              if (err === 'cancelled' || err === 'Выгрузка остановлена пользователем') {
                return (
                  <div className="ds-chat__loading ds-fault" id="ds-expired">
                    <div className="ds-fault__ico ds-fault__ico--warn">
                      {IconWarnTriangle}
                    </div>
                    <h2>Выгрузка остановлена</h2>
                    <p>Сбор сообщений по выбранным источникам был остановлен по вашему запросу.</p>
                    <div className="ds-fault__actions">
                      <button
                        type="button"
                        className="btn btn--primary"
                        onClick={handleRefreshSession}
                        disabled={refreshSessionMutation.isPending}
                      >
                        {refreshSessionMutation.isPending ? 'Запускаю…' : 'Запустить выгрузку заново'}
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={(e) => activeSession && handleDeleteSession(e, activeSession.id, activeSession.title)}
                      >
                        Удалить чат
                      </button>
                    </div>
                  </div>
                )
              }
              if (err === 'no_userbot') {
                return (
                  <div className="ds-chat__loading ds-fault" id="ds-expired">
                    <div className="ds-fault__ico ds-fault__ico--warn">
                      {IconWarnTriangle}
                    </div>
                    <h2>Сессия Telegram недоступна</h2>
                    <p>Не удалось подключиться к вашему Telegram-аккаунту, поэтому сбор не выполнен. Переподключите бота и создайте чат заново — уже собранные данные сохранены.</p>
                    <div className="ds-fault__actions">
                      <a
                        href="https://t.me/sensor_tg_bot"
                        className="btn btn--primary"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {IconTelegramPlane}
                        Переподключить Telegram
                      </a>
                      <button type="button" className="btn btn--ghost" onClick={() => setModalOpen(true)}>
                        Создать заново
                      </button>
                    </div>
                  </div>
                )
              }
              return (
                <div className="ds-chat__loading ds-fault" id="ds-expired">
                  <div className="ds-fault__ico">
                    {IconAlertCircle}
                  </div>
                  <h2>{err === 'no_messages' ? 'Сообщения не собраны' : 'Не удалось собрать сообщения'}</h2>
                  <p>
                    {err === 'no_messages'
                      ? 'В выбранных группах не нашлось сообщений за указанный период. Попробуйте другой период или другие источники.'
                      : 'Произошла ошибка при сборе. Попробуйте создать чат заново.'}
                  </p>
                  <div className="ds-fault__actions">
                    <button type="button" className="btn btn--primary" onClick={() => setModalOpen(true)}>
                      Создать заново
                    </button>
                  </div>
                </div>
              )
            })()}
          </div>

          {/* Composer */}
          <div className="ds-chat__composer" id="chat-composer">
            <div className="ds-chat__composer-inner">
              <div className="ds-chat__input">
                <textarea
                  ref={textareaRef}
                  id="chat-input"
                  rows={1}
                  placeholder="Задавайте вопрос..."
                  value={inputValue}
                  disabled={isWaiting || chatState !== 'thread'}
                  onChange={(e) => {
                    setInputValue(e.target.value)
                    autoGrow()
                  }}
                  onKeyDown={handleKeyDown}
                />
                <button
                  type="button"
                  className="ds-chat__send"
                  id="chat-send"
                  aria-label="Отправить"
                  disabled={!inputValue.trim() || isWaiting || chatState !== 'thread'}
                  onClick={handleSend}
                >
                  {IconSend}
                </button>
              </div>
              <div className="ds-chat__composer__hint">
                {IconInfo}
                Ответы строятся только по собранным сообщениям и сопровождаются
                ссылками на исходные посты.
              </div>
            </div>
          </div>
        </section>

        {/* ============================================================
            RIGHT RAIL — context
            ============================================================ */}
        <ContextRail session={sessionDetail} onToggle={toggleRight} />

      </section>

      {/* Drawer scrim (mobile/tablet) */}
      <div
        className={`ds-chat__drawer-scrim${drawerScrimOpen ? ' is-open' : ''}`}
        id="drawer-scrim"
        onClick={closeDrawers}
      />

      {/* New-chat wizard */}
      <NewChatModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={handleSessionCreated}
        userTimezone={userTimezone}
        canExportDialogs={['pro', 'team', 'enterprise'].includes(user?.tier ?? '')}
      />
      <NewTaskModal
        isOpen={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        onCreated={handleTaskCreated}
      />
      <DeleteConfirmDialog
        target={deleteConfirm}
        isBusy={deleteSessionMutation.isPending || deleteAllMutation.isPending}
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={confirmDelete}
      />
    </div>
  )
}
